<template>
  <div class="planning-page">
    <header class="planning-toolbar">
      <div class="planning-toolbar__left">
        <h1 class="planning-toolbar__title">Planning</h1>
        <div class="view-toggle">
          <button :class="{ active: currentView === 'week' }" @click="switchView('week')">
            Week
          </button>
          <button :class="{ active: currentView === 'day' }" @click="switchView('day')">Dag</button>
        </div>
        <template v-if="currentView === 'day'">
          <button class="toolbar-nav-btn" @click="navigateDay(-1)" title="Vorige dag">‹</button>
          <span class="planning-toolbar__date">{{ dayViewTitle }}</span>
          <button class="toolbar-nav-btn" @click="navigateDay(1)" title="Volgende dag">›</button>
        </template>
        <template v-else>
          <button class="toolbar-nav-btn" @click="navigateWeek(-1)" title="Vorige week">‹</button>
          <span class="planning-toolbar__date">{{ weekViewTitle }}</span>
          <button class="toolbar-nav-btn" @click="navigateWeek(1)" title="Volgende week">›</button>
        </template>
        <button class="toolbar-nav-btn toolbar-nav-btn--today" @click="goToTodayView">
          Vandaag
        </button>
      </div>
      <div class="planning-toolbar__right">
        <Btn
          variant="outlined"
          icon="truck"
          @click="$router.push('/planning/materieel')"
          :class="{ 'planning-toolbar__btn--warn': resourceConflictCount > 0 }"
          :title="
            resourceConflictCount > 0
              ? $t('resources.planning.conflicts_in_view', { count: resourceConflictCount })
              : $t('resources.timeline.title')
          "
        >
          {{ $t('resources.title') }}
          <template v-if="resourceConflictCount > 0">({{ resourceConflictCount }})</template>
        </Btn>
        <Btn
          variant="outlined"
          icon="tool"
          @click="goToServiceNotifications"
          title="Teruggekomen werkbonnen en servicebonnen"
        >
          Servicemeldingen
        </Btn>
        <Btn
          :variant="showUnplannedDrawer ? 'primary' : 'secondary'"
          icon="columns"
          @click="toggleUnplannedPanel"
          :class="{ 'planning-toolbar__btn--badge': planningQueueCount > 0 }"
          :title="showUnplannedDrawer ? 'Paneel verbergen' : planningQueueTooltip"
        >
          {{ planningQueueButtonLabel }}
        </Btn>
        <Btn variant="secondary" @click="refreshWorkOrders" icon="redo" :loading="loading">
          Vernieuwen
        </Btn>
      </div>
    </header>
    <PlanningRevenueBar
      :view="currentView"
      :view-value="plannedViewValue"
      :month-value="plannedMonthValue"
      :avg-per-day="plannedAvgPerDay"
      :loading-view="loading && workOrders.length === 0"
      :loading-month="loading && workOrders.length === 0"
      @view-click="openRevenueDrawer('view')"
      @month-click="openRevenueDrawer('month')"
    />
    <PlanningRevenueDrawer
      :show="showRevenueDrawer"
      :period-label="revenueDrawerPeriodLabel"
      :summary="revenueDrawerSummary"
      :items="revenueDrawerItems"
      @close="showRevenueDrawer = false"
      @select="handleRevenueSelect"
    />
    <div class="planning-page__content">
      <!-- Boven de grid: monteurs (alle, disabled als uitgevinkt) + status filter -->
      <div class="planning-page__main">
        <div class="planning-top-row">
          <!-- Monteurs filter trigger -->
          <button
            ref="monteursFilterRef"
            class="planning-filter-btn"
            :class="{ 'planning-filter-btn--active': hiddenInstallerIds.size > 0 }"
            type="button"
          >
            <BaseIcon name="user" size="13" />
            <span>{{ monteursDropdownLabel }}</span>
            <BaseIcon
              name="chevron"
              size="12"
              class="planning-filter-btn__chevron"
              :class="{ 'planning-filter-btn__chevron--open': monteursFilterOpen }"
            />
          </button>

          <!-- Status filter trigger -->
          <button
            ref="statusFilterRef"
            class="planning-filter-btn"
            :class="{
              'planning-filter-btn--active': visibleStatusKeys.size < statusLegendItems.length,
            }"
            type="button"
          >
            <span>{{ statusDropdownLabel }}</span>
            <BaseIcon
              name="chevron"
              size="12"
              class="planning-filter-btn__chevron"
              :class="{ 'planning-filter-btn__chevron--open': statusFilterOpen }"
            />
          </button>

          <!-- Werktype filter trigger -->
          <button
            ref="workTypeFilterRef"
            class="planning-filter-btn"
            :class="{
              'planning-filter-btn--active': visibleWorkTypeKeys.size < workTypeLegendItems.length,
            }"
            type="button"
          >
            <span>{{ workTypeDropdownLabel }}</span>
            <BaseIcon
              name="chevron"
              size="12"
              class="planning-filter-btn__chevron"
              :class="{ 'planning-filter-btn__chevron--open': workTypeFilterOpen }"
            />
          </button>
        </div>

        <!-- Monteurs filter panel (Tippy content) -->
        <div ref="monteursFilterPanelRef" class="planning-filter-panel">
          <div class="planning-filter-panel__actions">
            <button @click.stop="showAllInstallers">Alle tonen</button>
            <button @click.stop="hideAllInstallers">Alle verbergen</button>
          </div>
          <div
            v-for="item in installersWithUnassigned"
            :key="item.id"
            class="planning-filter-item"
            :class="{ 'planning-filter-item--inactive': !isInstallerVisible(item.id) }"
            @click.stop="toggleInstaller(item.id)"
          >
            <span class="planning-filter-item__dot" :style="{ backgroundColor: item.color }" />
            <span class="planning-filter-item__label">{{ item.label }}</span>
            <span class="planning-filter-item__count">
              {{ getWorkOrderCountForInstaller(item.id) }}
            </span>
          </div>
        </div>

        <!-- Status filter panel (Tippy content) -->
        <div ref="statusFilterPanelRef" class="planning-filter-panel">
          <div class="planning-filter-panel__actions">
            <button @click.stop="showAllStatuses">Alle tonen</button>
            <button @click.stop="hideAllStatuses">Alle verbergen</button>
          </div>
          <div
            v-for="s in statusLegendItems"
            :key="s.key"
            class="planning-filter-item"
            :class="{ 'planning-filter-item--inactive': !isStatusVisible(s.key) }"
            @click.stop="toggleStatusFilter(s.key)"
          >
            <span
              class="planning-filter-item__dot"
              :class="'planning-filter-item__dot--' + s.key"
            />
            <span class="planning-filter-item__label">{{ s.label }}</span>
          </div>
        </div>

        <!-- Werktype filter panel (Tippy content) -->
        <div ref="workTypeFilterPanelRef" class="planning-filter-panel">
          <div class="planning-filter-panel__actions">
            <button @click.stop="showAllWorkTypes">Alle tonen</button>
            <button @click.stop="hideAllWorkTypes">Alle verbergen</button>
          </div>
          <div
            v-for="t in workTypeLegendItems"
            :key="t.key"
            class="planning-filter-item"
            :class="{ 'planning-filter-item--inactive': !isWorkTypeVisible(t.key) }"
            @click.stop="toggleWorkTypeFilter(t.key)"
          >
            <span
              class="planning-filter-item__dot"
              :class="'planning-filter-item__dot--' + t.key"
            />
            <span class="planning-filter-item__label">{{ t.label }}</span>
            <span class="planning-filter-item__count">
              {{ getWorkOrderCountForWorkType(t.key) }}
            </span>
          </div>
        </div>

        <!-- Dag view: monteurs boven (X), tijden links (Y) -->
        <div v-if="currentView === 'day'" class="planning-page__calendar day-cal">
          <!-- Sticky kolomheader -->
          <div class="day-cal__header" :style="{ '--day-cols': visibleInstallersForGrid.length }">
            <div class="day-cal__header-time"></div>
            <div
              v-for="item in visibleInstallersForGrid"
              :key="item.id"
              class="day-cal__header-cell"
            >
              <span class="cell-header__dot" :style="{ backgroundColor: item.color }" />
              <span class="cell-header__name">{{ item.label }}</span>
              <span class="cell-header__count">{{ getWorkOrderCountForDay(item.id) }} bonnen</span>
              <span
                v-if="dayHeaderHours(item.id)"
                class="cell-header__hours"
                :class="{
                  'cell-header__hours--custom': dayHeaderHours(item.id).custom,
                  'cell-header__hours--off': dayHeaderHours(item.id).off,
                }"
                :title="
                  dayHeaderHours(item.id).custom
                    ? 'Eigen werktijden (Instellingen → Team)'
                    : 'Werktijd organisatie'
                "
              >
                <BaseIcon name="clock" size="10" />
                {{ dayHeaderHours(item.id).label }}
              </span>
            </div>
          </div>
          <!-- Scrollbare body -->
          <div class="day-cal__body">
            <div
              class="day-cal__grid"
              :style="{ '--day-cols': visibleInstallersForGrid.length }"
              @dragleave="onCalendarDragLeave"
            >
              <template v-for="slot in dayTimeSlots" :key="slot.hour">
                <!-- Tijdlabel kolom -->
                <div
                  class="day-cal__time"
                  :class="{
                    'day-cal__time--hour': slot.isHour,
                    'day-cal__time--half': slot.isHalfHour,
                  }"
                >
                  <span v-if="slot.isHour">{{ slot.label }}</span>
                </div>
                <!-- Cel per monteur -->
                <div
                  v-for="item in visibleInstallersForGrid"
                  :key="item.id"
                  class="day-cal__cell"
                  :class="[
                    {
                      'day-cal__cell--hour': slot.isHour,
                      'day-cal__cell--half': slot.isHalfHour,
                      'day-cal__cell--dragover': draggingWorkOrder,
                    },
                    dayCellOffClass(item.id, slot),
                    dragWindowCellClass(selectedDayDate, slot.hour, 'day-cal__cell'),
                  ]"
                  @click="onDayCellClick(slot.hour, item.id)"
                  @dragover.prevent="onDayCellDragOver($event, slot.hour, item.id)"
                  @drop.prevent="onDayCellDrop($event, slot.hour, item.id)"
                >
                  <div
                    v-for="wo in getWorkOrdersForDayCell(item.id, slot.hour)"
                    :key="wo.id"
                    class="planning-event planning-event--day"
                    :class="{
                      'planning-event--tentative': wo.isTentative,
                      'planning-event--unassigned': wo.isUnassigned,
                      'planning-event--helper': wo.isHelper,
                      'planning-event--has-indicators': wo.isConfirmed || wo.materials !== 'none',
                      'planning-event--drag-hidden': wo.id === dragSourceHiddenId,
                    }"
                    :style="{
                      height: wo.heightPx + 'px',
                      minHeight: wo.heightPx + 'px',
                      backgroundColor: wo.palette.bg,
                      color: wo.palette.text,
                      borderColor: wo.palette.border,
                      ...(dayEventLaneStyle(wo) || {}),
                    }"
                    draggable="true"
                    @dragstart.stop="(e) => handleDragStart(e, wo.workOrder)"
                    @dragend.stop="handleDragEnd"
                    @click.stop="handleEventClick(wo.workOrder)"
                  >
                    <span class="planning-event__indicators">
                      <span
                        v-if="wo.isTentative"
                        class="planning-event__badge"
                        title="Voorlopig ingepland — nog niet bevestigd met klant"
                      >
                        Voorlopig
                      </span>
                      <span
                        v-else-if="wo.isHelper"
                        class="planning-event__badge planning-event__badge--helper"
                        title="Hulpmonteur op deze werkbon — hoofdmonteur staat in een andere kolom"
                      >
                        Hulp
                      </span>
                      <span
                        v-if="wo.laneCount > 1"
                        class="planning-event__indicator planning-event__indicator--overlap"
                        title="Overlapt met een andere afspraak van deze monteur"
                      >
                        <BaseIcon name="exclamation-triangle" size="9" />
                      </span>
                      <span
                        v-if="wo.isConfirmed"
                        class="planning-event__indicator planning-event__indicator--confirmed"
                        title="Afspraak bevestigd met klant"
                      >
                        <BaseIcon name="check" size="9" />
                      </span>
                      <span
                        v-if="wo.materials !== 'none'"
                        class="planning-event__indicator planning-event__indicator--materials"
                        :class="{
                          'planning-event__indicator--materials-waiting':
                            wo.materials === 'waiting',
                        }"
                        :title="
                          wo.materials === 'in' ? 'Materialen binnen' : 'Materialen nog niet binnen'
                        "
                      >
                        <BaseIcon name="box" size="9" />
                      </span>
                      <span
                        v-if="wo.resourceConflict"
                        class="planning-event__indicator planning-event__indicator--resource"
                        :title="
                          $t('resources.planning.conflict_tooltip', { detail: wo.resourceConflict })
                        "
                      >
                        <BaseIcon name="cubes" size="9" />
                      </span>
                    </span>
                    <span class="planning-event__customer">{{ wo.customer }}</span>
                    <span class="planning-event__time">
                      <template v-if="wo.revenueLabel">{{ wo.revenueLabel }} ·</template>
                      {{ wo.timeRange }}
                    </span>
                    <button
                      type="button"
                      class="planning-event__edit"
                      title="Verzetten"
                      @click.stop="openScheduleModal(wo.workOrder)"
                    >
                      <BaseIcon name="edit" size="11" />
                    </button>
                  </div>
                  <div
                    v-if="
                      dragGhost &&
                      dragGhost.type === 'day' &&
                      dragGhost.renderHour === slot.hour &&
                      dragGhost.installerId === item.id
                    "
                    class="planning-event planning-event--day planning-event--ghost"
                    :class="'planning-event--ghost-' + dragGhost.state"
                    :style="{
                      height: dragGhost.heightPx + 'px',
                      minHeight: dragGhost.heightPx + 'px',
                      backgroundColor: dragGhost.palette.bg,
                      color: dragGhost.palette.text,
                      borderColor: dragGhost.palette.border,
                    }"
                  >
                    <span class="planning-event__customer">{{ dragGhost.customer }}</span>
                    <span class="planning-event__time">{{ dragGhost.timeRange }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Week view: monteurs links (Y), dagen boven (X) -->
        <div v-else-if="currentView === 'week'" class="planning-page__calendar planning-grid-wrap">
          <div class="planning-grid-header planning-grid-header--hidden">
            <div class="view-toggle">
              <button :class="{ active: currentView === 'week' }" @click="switchView('week')">
                Week
              </button>
              <button :class="{ active: currentView === 'day' }" @click="switchView('day')">
                Dag
              </button>
            </div>
            <button class="nav-btn" @click="navigateWeek(-1)" title="Vorige week">‹</button>
            <h2 class="planning-grid-title">{{ weekViewTitle }}</h2>
            <button class="nav-btn" @click="navigateWeek(1)" title="Volgende week">›</button>
            <button class="nav-btn nav-btn--today" @click="goToTodayView">Vandaag</button>
          </div>
          <div class="planning-grid planning-grid--week" @dragleave="onCalendarDragLeave">
            <div class="planning-grid__row planning-grid__row--header">
              <div class="planning-grid__cell planning-grid__cell--resource-label"></div>
              <div
                v-for="day in weekDaysForView"
                :key="day.date"
                class="planning-grid__cell planning-grid__cell--header"
                :class="{ 'planning-grid__cell--today': day.isToday }"
              >
                <span class="cell-header__day">{{ day.dayName }}</span>
                <span class="cell-header__date">{{ day.dateShort }}</span>
                <span
                  v-if="getDayFreeCapacity(day.date)"
                  class="cell-header__capacity"
                  :class="{ 'cell-header__capacity--empty': !getDayFreeCapacity(day.date).minutes }"
                  title="Vrije werktijd van de zichtbare monteurs op deze dag"
                >
                  {{ getDayFreeCapacity(day.date).label }}
                </span>
                <span
                  v-if="getWindowDemand(day.date)"
                  class="cell-header__demand"
                  :class="{ 'cell-header__demand--hard': getWindowDemand(day.date).onlyCount }"
                  :title="getWindowDemand(day.date).title"
                >
                  <BaseIcon name="calendar-clock" size="11" />
                  {{ getWindowDemand(day.date).label }}
                </span>
              </div>
            </div>
            <div v-for="item in visibleInstallersForGrid" :key="item.id" class="planning-grid__row">
              <div class="planning-grid__cell planning-grid__cell--resource-label">
                <span class="resource-label__name">{{ item.label }}</span>
                <span class="resource-label__count">{{ getWorkOrderCountForWeek(item.id) }}</span>
              </div>
              <div
                v-for="day in weekDaysForView"
                :key="`${item.id}-${day.date}`"
                class="planning-grid__cell planning-grid__cell--drop"
                :class="[
                  {
                    'planning-grid__cell--today': day.isToday,
                    'planning-grid__cell--drop-active': draggingWorkOrder,
                    'planning-grid__cell--day-off': isTechDayOff(item.id, day.date),
                    'planning-grid__cell--pending':
                      pendingSlot?.type === 'week' &&
                      pendingSlot?.date === day.date &&
                      pendingSlot?.installerId === item.id,
                  },
                  dragWindowCellClass(day.date, null, 'planning-grid__cell'),
                ]"
                :title="
                  isTechDayOff(item.id, day.date)
                    ? `${item.label} is vrij op deze dag`
                    : dragWindowCellTitle(day.date)
                "
                @click="onWeekCellClick(day.date, item.id)"
                @dragover.prevent="onWeekCellDragOver($event, day.date, item.id)"
                @drop.prevent="onWeekCellDrop($event, day.date, item.id)"
              >
                <div
                  v-for="wo in getWorkOrdersForWeekCell(item.id, day.date)"
                  :key="wo.id"
                  class="planning-event planning-event--week"
                  :class="{
                    'planning-event--tentative': wo.isTentative,
                    'planning-event--unassigned': wo.isUnassigned,
                    'planning-event--helper': wo.isHelper,
                    'planning-event--has-indicators': wo.isConfirmed || wo.materials !== 'none',
                    'planning-event--drag-hidden': wo.id === dragSourceHiddenId,
                  }"
                  :style="{
                    backgroundColor: wo.palette.bg,
                    color: wo.palette.text,
                    borderColor: wo.palette.border,
                  }"
                  draggable="true"
                  @dragstart.stop="(e) => handleDragStart(e, wo.workOrder)"
                  @dragend.stop="handleDragEnd"
                  @click.stop="handleEventClick(wo.workOrder)"
                >
                  <span class="planning-event__indicators">
                    <span
                      v-if="wo.isTentative"
                      class="planning-event__badge"
                      title="Voorlopig ingepland — nog niet bevestigd met klant"
                    >
                      Voorlopig
                    </span>
                    <span
                      v-else-if="wo.isHelper"
                      class="planning-event__badge planning-event__badge--helper"
                      title="Hulpmonteur op deze werkbon — hoofdmonteur staat in een andere kolom"
                    >
                      Hulp
                    </span>
                    <span
                      v-if="wo.isConfirmed"
                      class="planning-event__indicator planning-event__indicator--confirmed"
                      title="Afspraak bevestigd met klant"
                    >
                      <BaseIcon name="check" size="9" />
                    </span>
                    <span
                      v-if="wo.materials !== 'none'"
                      class="planning-event__indicator planning-event__indicator--materials"
                      :class="{
                        'planning-event__indicator--materials-waiting': wo.materials === 'waiting',
                      }"
                      :title="
                        wo.materials === 'in' ? 'Materialen binnen' : 'Materialen nog niet binnen'
                      "
                    >
                      <BaseIcon name="box" size="9" />
                    </span>
                    <span
                      v-if="wo.resourceConflict"
                      class="planning-event__indicator planning-event__indicator--resource"
                      :title="
                        $t('resources.planning.conflict_tooltip', { detail: wo.resourceConflict })
                      "
                    >
                      <BaseIcon name="cubes" size="9" />
                    </span>
                  </span>
                  <span class="planning-event__customer">{{ wo.customer }}</span>
                  <span class="planning-event__time">
                    <template v-if="wo.revenueLabel">{{ wo.revenueLabel }} ·</template>
                    {{ wo.timeRange }}
                  </span>
                  <button
                    type="button"
                    class="planning-event__edit"
                    title="Verzetten"
                    @click.stop="openScheduleModal(wo.workOrder)"
                  >
                    <BaseIcon name="edit" size="11" />
                  </button>
                </div>
                <div
                  v-if="
                    dragGhost &&
                    dragGhost.type === 'week' &&
                    dragGhost.date === day.date &&
                    dragGhost.installerId === item.id
                  "
                  class="planning-event planning-event--week planning-event--ghost"
                  :class="'planning-event--ghost-' + dragGhost.state"
                  :style="{
                    backgroundColor: dragGhost.palette.bg,
                    color: dragGhost.palette.text,
                    borderColor: dragGhost.palette.border,
                  }"
                >
                  <span class="planning-event__customer">{{ dragGhost.customer }}</span>
                  <span class="planning-event__time">{{ dragGhost.timeRange }}</span>
                </div>
                <div
                  v-else-if="
                    pendingSlot?.type === 'week' &&
                    pendingSlot?.date === day.date &&
                    pendingSlot?.installerId === item.id
                  "
                  class="planning-slot-placeholder"
                >
                  <BaseIcon name="plus" size="16" />
                </div>
                <div
                  v-else-if="getWorkOrdersForWeekCell(item.id, day.date).length === 0"
                  class="planning-cell-hover-plus"
                >
                  <BaseIcon name="plus" size="14" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Gedockt "Te plannen"-paneel: bron voor drag & drop naar de kalender,
           en dropzone om geplande kaarten weer los te maken. -->
      <PlanningUnplannedPanel
        :show="showUnplannedDrawer"
        :unplanned-work-orders="filteredUnplannedForDrawer"
        :tentative-work-orders="filteredTentativeForDrawer"
        :get-status-label="getStatusLabel"
        :format-date-time="formatDateTime"
        :pick-mode="!!pendingSlot"
        :pick-label="pendingSlotLabel"
        :pick-date="pendingSlotDate"
        :selected-ids="selectedWorkOrderIds"
        :selected-count="selectedWorkOrderIds.size"
        :selection-summary="selectionSummary"
        :bulk-loading="bulkLoading"
        :bulk-active-action="bulkActiveAction"
        :unschedule-drop-active="isDraggingPlannedWorkOrder"
        @close="closeUnplannedDrawer"
        @schedule="openScheduleModal"
        @select="(wo) => openWorkOrderDetail(wo)"
        @pick="handlePickWorkOrder"
        @delete="handleDeleteWorkOrder"
        @dragstart="(e, wo) => handleDragStart(e, wo)"
        @dragend="handleDragEnd"
        @unschedule="handleUnscheduleDrop"
        @toggle-selection="toggleWorkOrderSelection"
        @select-visible="setWorkOrderSelection"
        @select-ids="addWorkOrderSelection"
        @deselect-ids="removeWorkOrderSelection"
        @clear-selection="clearWorkOrderSelection"
        @bulk-confirm="handleBulkConfirm"
        @bulk-delete="handleBulkDelete"
        @open-bulk-plan="handleOpenBulkPlanModal"
      />
    </div>

    <div v-if="loading" class="loading-overlay">
      <LoadingSpinner />
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import tippy from 'tippy.js';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@shared/components/LoadingSpinner.vue';

  import { workOrderService } from '@/services/workOrderService';
  import { resourceService } from '@/services/resourceService';
  import OrganizationService from '@/services/organizationService';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useWorkOrderDelete } from '@/composables/useWorkOrderDelete';
  import {
    statusAfterScheduling,
    getAppointmentStart,
    isAppointmentConfirmed,
    isPlanned,
    isTentativelyPlanned,
    getLifecycleStatus,
    getDerivedState,
    getMaterialsState,
    buildAppointmentPayload,
    applyAppointmentLocal,
    scheduleErrorMessage,
  } from '@/utils/workOrders';
  import ScheduleWorkOrderModal from '@/components/work-orders/ScheduleWorkOrderModal.vue';
  import WorkOrderDetailDrawer from '@/components/work-orders/WorkOrderDetailDrawer.vue';
  import PlanningUnplannedPanel from '@/components/planning/PlanningUnplannedPanel.vue';
  import PlanningRevenueBar from '@/components/planning/PlanningRevenueBar.vue';
  import PlanningRevenueDrawer from '@/components/planning/PlanningRevenueDrawer.vue';
  import PlanningBulkPlanModal from '@/components/planning/PlanningBulkPlanModal.vue';
  import RoutePlanningModal from '@/components/planning/RoutePlanningModal.vue';
  import PlanningHelpersBusyModal from '@/components/planning/PlanningHelpersBusyModal.vue';
  import {
    ALL_PLANNING_WORK_TYPE_KEYS,
    PLANNING_WORK_TYPE_OPTIONS,
    ROUTE_PLANNING_MIN_LOADING_MS,
    STORAGE_KEY_PLANNING_WORK_TYPES,
    getPlanningWorkTime,
  } from '@/constants/planningConstants';
  import { formatCurrency, getISOWeek } from '@/utils/statsFormat';
  import { getCityGroup } from '@/utils/planningGroups';
  import {
    getWorkOrderWindow,
    evaluatePlanningWindow,
    formatPlanningWindow,
    isoWeekday,
  } from '@/utils/planningWindow';
  import { resolveTechnicianWorkTime, getMemberPlanningSettings } from '@/utils/technicianWorkTime';
  import {
    PLACEMENT_STRATEGIES,
    appointmentLocalMinutes,
    assignLanes,
    busyIntervalsForDay,
    findBlockingHelpers,
    formatMinutes,
    freeMinutesInWorkday,
    minutesToHm,
    parseHmToMinutes,
    placeInDay,
    workOrderDuration,
  } from '@/utils/planningSlots';

  export default {
    name: 'PlanningPage',

    components: {
      Btn,
      BaseIcon,
      LoadingSpinner,
      ScheduleWorkOrderModal,
      PlanningUnplannedPanel,
      PlanningRevenueBar,
      PlanningRevenueDrawer,
    },

    setup() {
      const router = useRouter();
      const userStore = useUserStore();
      const isAdmin = computed(() =>
        ['admin', 'super_admin', 'owner'].includes(userStore.currentOrganization?.role)
      );
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const $t = getCurrentInstance().appContext.config.globalProperties.$t;

      const goToServiceNotifications = () => {
        router.push('/servicemeldingen');
      };

      const workOrders = ref([]);
      const selectedWorkOrder = ref(null);
      const loading = ref(false);
      const loadedRanges = ref(new Set());
      const currentView = ref('week');
      const draggingWorkOrder = ref(null);

      // Het "Te plannen"-paneel staat gedockt naast de kalender. Open/dicht
      // is een voorkeur van de planner en overleeft een reload. Standaard open.
      const PANEL_OPEN_KEY = 'planning_unplanned_panel_open';
      const readPanelPreference = () => {
        try {
          const raw = localStorage.getItem(PANEL_OPEN_KEY);
          return raw === null ? true : raw === '1';
        } catch {
          return true;
        }
      };
      const showUnplannedDrawer = ref(readPanelPreference());
      // Paneel kan tijdelijk geopend zijn voor een pick (klik op lege cel);
      // dan sluit het na de keuze weer, anders blijft het staan.
      const panelOpenedForPick = ref(false);

      const setPanelOpen = (open, { persist = true } = {}) => {
        showUnplannedDrawer.value = open;
        if (!persist) return;
        try {
          localStorage.setItem(PANEL_OPEN_KEY, open ? '1' : '0');
        } catch {
          // Storage niet beschikbaar — alleen in-memory.
        }
      };

      const toggleUnplannedPanel = () => {
        if (showUnplannedDrawer.value) {
          closeUnplannedDrawer();
        } else {
          setPanelOpen(true);
        }
      };

      const openPanelForPick = () => {
        if (!showUnplannedDrawer.value) {
          panelOpenedForPick.value = true;
          setPanelOpen(true, { persist: false });
        }
      };

      const finishPick = () => {
        pendingSlot.value = null;
        if (panelOpenedForPick.value) {
          panelOpenedForPick.value = false;
          setPanelOpen(false, { persist: false });
        }
      };
      const organizationService = new OrganizationService();
      const installers = ref([]);
      const workOrderToSchedule = ref(null);

      // Dag view: geselecteerde dag (ISO YYYY-MM-DD)
      const selectedDayDate = ref(new Date().toISOString().split('T')[0]);
      // Week view: maandag van de week (Date)
      const getMonday = (d) => {
        const date = new Date(d);
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.setDate(diff));
      };
      const weekStartDate = ref(getMonday(new Date()));
      const showRevenueDrawer = ref(false);
      const revenueDrawerScope = ref('view');

      const toLocalIsoDate = (value) => {
        const d = value instanceof Date ? value : new Date(`${value}T12:00:00`);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };

      const addDaysIso = (iso, days) => {
        const d = new Date(`${iso}T12:00:00`);
        d.setDate(d.getDate() + days);
        return toLocalIsoDate(d);
      };

      const plannedRange = computed(() => {
        if (currentView.value === 'day') {
          return { from: selectedDayDate.value, to: selectedDayDate.value };
        }
        const from = toLocalIsoDate(weekStartDate.value);
        return { from, to: addDaysIso(from, 6) };
      });

      const monthRange = computed(() => {
        const iso =
          currentView.value === 'day' ? selectedDayDate.value : toLocalIsoDate(weekStartDate.value);
        const d = new Date(`${iso}T12:00:00`);
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const days = new Date(year, month, 0).getDate();
        const mm = String(month).padStart(2, '0');
        return {
          from: `${year}-${mm}-01`,
          to: `${year}-${mm}-${String(days).padStart(2, '0')}`,
          days,
        };
      });

      const STORAGE_KEY_HIDDEN = 'smartflow_planning_hidden_installers';
      const STORAGE_KEY_STATUSES = 'smartflow_planning_visible_statuses';
      const STORAGE_KEY_VIEW = 'smartflow_planning_view';

      const loadSet = (key) => {
        try {
          const raw = localStorage.getItem(key);
          return raw ? new Set(JSON.parse(raw)) : null;
        } catch {
          return null;
        }
      };
      const saveSet = (key, set) => {
        localStorage.setItem(key, JSON.stringify([...set]));
      };

      const workTypeLegendItems = PLANNING_WORK_TYPE_OPTIONS;
      const initVisibleWorkTypeKeys = () => {
        const saved = loadSet(STORAGE_KEY_PLANNING_WORK_TYPES);
        if (!saved) return new Set(ALL_PLANNING_WORK_TYPE_KEYS);
        const normalized = new Set(
          [...saved].filter((key) => ALL_PLANNING_WORK_TYPE_KEYS.includes(key))
        );
        return normalized.size > 0 ? normalized : new Set(ALL_PLANNING_WORK_TYPE_KEYS);
      };
      const visibleWorkTypeKeys = ref(initVisibleWorkTypeKeys());

      const selectedWorkOrderIds = ref(new Set());
      const bulkLoading = ref(false);
      const bulkActiveAction = ref(null);

      const savedView = localStorage.getItem(STORAGE_KEY_VIEW);
      if (savedView === 'day' || savedView === 'week') {
        currentView.value = savedView;
      }

      const hiddenInstallerIds = ref(loadSet(STORAGE_KEY_HIDDEN) || new Set());

      const INSTALLER_DOT_COLORS = [
        '#3b82f6',
        '#10b981',
        '#f59e0b',
        '#8b5cf6',
        '#ef4444',
        '#06b6d4',
        '#ec4899',
        '#7c3aed',
      ];

      const STATUS_PALETTES = {
        pending: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
        scheduled: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
        ready_for_installation: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
        in_progress: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
        completed: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
        cancelled: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
      };
      // Kaartkleur = levenscyclus, niet de afgeleide state. Alles wat open is
      // en op de kalender staat is dus geel — bevestiging en materialen worden
      // door de icoontjes verteld, voorlopig door de gestreepte rand. Grijs
      // ('pending') hoort alleen bij óngeplande bonnen en komt op de kalender
      // niet meer voor.
      const getStatusPalette = (wo) => {
        const lifecycle = getLifecycleStatus(wo);
        if (lifecycle === 'open') return STATUS_PALETTES.scheduled;
        return STATUS_PALETTES[lifecycle] || STATUS_PALETTES.pending;
      };

      // Vinkje "bevestigd met klant" is alleen relevant zolang de bon nog
      // niet gestart/afgerond is; daarna zegt de status zelf al genoeg.
      const isConfirmedForCard = (wo) =>
        isAppointmentConfirmed(wo) && getLifecycleStatus(wo) === 'open';

      // Load installers from organization members
      const loadInstallers = async () => {
        try {
          if (!userStore.currentOrganization?.id) return;
          const members =
            (await organizationService.getMembers(userStore.currentOrganization.id)) || [];
          installers.value = members;
        } catch (error) {
          console.error('Error loading installers:', error);
        }
      };

      // Helper: display name from member (first_name + last_name, fallback to email)
      const getMemberDisplayName = (member) => {
        if (!member?.user) return member?.email || 'Onbekend';
        const name = `${member.user.first_name || ''} ${member.user.last_name || ''}`.trim();
        return name || member.user.email || 'Onbekend';
      };

      // User ID for assignee (backend expects user_id, not user_organization id)
      const getInstallerUserId = (installer) => installer.user?.id ?? installer.id;

      // planning_settings per user_id (werktijden / inplanbaar), uit de members-API.
      const installerSettingsById = computed(() => {
        const map = new Map();
        for (const installer of installers.value) {
          map.set(getInstallerUserId(installer), installer.planning_settings || null);
        }
        return map;
      });

      // Iemand die op "niet inplanbaar" staat verdwijnt uit de planning, tenzij
      // er nog bonnen op zijn/haar naam staan — die mogen nooit onzichtbaar worden.
      const isInstallerSchedulable = (installer) => {
        const settings = getMemberPlanningSettings(installer.planning_settings);
        if (settings.schedulable) return true;
        const ids = [installer.id, installer.user?.id].filter(Boolean);
        return workOrders.value.some((wo) => {
          const all = getAllAssigneeIds(wo);
          return ids.some((id) => all.has(id));
        });
      };

      const schedulableInstallers = computed(() => installers.value.filter(isInstallerSchedulable));

      // Installer options for select dropdown (value = user_id for PATCH assigned_to)
      const installerOptions = computed(() => {
        return schedulableInstallers.value.map((installer) => ({
          value: getInstallerUserId(installer),
          label: getMemberDisplayName(installer),
        }));
      });

      // Sidebar: Monteurs + Niet toegewezen met kleur en count (id = user_id)
      const installersWithUnassigned = computed(() => {
        const list = schedulableInstallers.value.map((installer, index) => ({
          id: getInstallerUserId(installer),
          label: getMemberDisplayName(installer),
          color: INSTALLER_DOT_COLORS[index % INSTALLER_DOT_COLORS.length],
        }));
        list.unshift({
          id: 'unassigned',
          label: 'Niet toegewezen',
          color: '#64748b',
        });
        return list;
      });

      // Alleen zichtbare monteurs voor de grid (voor dag/week view)
      const visibleInstallersForGrid = computed(() =>
        installersWithUnassigned.value.filter((item) => isInstallerVisible(item.id))
      );

      // Werktijden van de organisatie — basis voor de werktijden per monteur.
      const workTime = computed(() => getPlanningWorkTime(userStore.currentOrganization));

      // Effectieve werktijd van één monteur op één dag (organisatie-standaard
      // overschreven door user_organizations.planning_settings).
      const techWorkTime = (installerId, dateStr) =>
        resolveTechnicianWorkTime(
          workTime.value,
          installerId && installerId !== 'unassigned'
            ? installerSettingsById.value.get(installerId) || null
            : null,
          dateStr
        );

      // Zichtbaar tijdvenster in de dagweergave: van de vroegste start tot de
      // laatste eindtijd van alle zichtbare monteurs (+1u marge), zodat iemand
      // die om 06:30 begint of tot 19:00 werkt gewoon in beeld is.
      const dayViewRange = computed(() => {
        const dateStr = selectedDayDate.value;
        let minStart = parseHmToMinutes(workTime.value.workday_start) ?? 480;
        let maxEnd = parseHmToMinutes(workTime.value.workday_end) ?? 1020;
        for (const item of visibleInstallersForGrid.value) {
          if (item.id === 'unassigned') continue;
          const wt = techWorkTime(item.id, dateStr);
          if (wt.day_off) continue;
          const s = parseHmToMinutes(wt.workday_start);
          const e = parseHmToMinutes(wt.workday_end);
          if (s != null) minStart = Math.min(minStart, s);
          if (e != null) maxEnd = Math.max(maxEnd, e);
        }
        const startMin = Math.max(0, Math.floor(minStart / 60) * 60 - 60);
        const endMin = Math.min(24 * 60, Math.ceil(maxEnd / 60) * 60 + 60);
        return { startMin, endMin };
      });

      const dayTimeSlots = computed(() => {
        const { startMin, endMin } = dayViewRange.value;
        const count = Math.floor((endMin - startMin) / 15) + 1;
        return Array.from({ length: count }, (_, i) => {
          const totalMinutes = startMin + i * 15;
          const h = Math.floor(totalMinutes / 60);
          const m = totalMinutes % 60;
          return {
            hour: h + m / 60,
            minutes: totalMinutes,
            rawHour: h,
            rawMinute: m,
            label: m === 0 ? `${String(h).padStart(2, '0')}:00` : '',
            isHour: m === 0,
            isHalfHour: m === 30,
          };
        });
      });

      // Per kolom: is dit kwartier buiten de werktijd van die monteur?
      const dayCellOffClass = (installerId, slot) => {
        if (installerId === 'unassigned') return null;
        const wt = techWorkTime(installerId, selectedDayDate.value);
        if (wt.day_off) return 'day-cal__cell--day-off';
        const s = parseHmToMinutes(wt.workday_start);
        const e = parseHmToMinutes(wt.workday_end);
        if (s == null || e == null) return null;
        if (slot.minutes < s || slot.minutes >= e) return 'day-cal__cell--off-hours';
        const lunch = wt.lunch_break;
        if (lunch) {
          const ls = parseHmToMinutes(lunch.start);
          const le = parseHmToMinutes(lunch.end);
          if (ls != null && le != null && slot.minutes >= ls && slot.minutes < le) {
            return 'day-cal__cell--lunch';
          }
        }
        return null;
      };

      // Kolomkop dagweergave: "08:00–17:00" of "Vrij"; afwijkend van de
      // organisatie-standaard wordt gemarkeerd.
      const dayHeaderHours = (installerId) => {
        if (installerId === 'unassigned') return null;
        const wt = techWorkTime(installerId, selectedDayDate.value);
        if (wt.day_off) return { label: 'Vrij', custom: true, off: true };
        return {
          label: `${wt.workday_start}–${wt.workday_end}`,
          custom: wt.source === 'member',
          off: false,
        };
      };

      // Weekweergave: vrije dag van een monteur arceren.
      const isTechDayOff = (installerId, dateStr) =>
        installerId !== 'unassigned' && techWorkTime(installerId, dateStr).day_off;

      // Vrije capaciteit op een dag over alle zichtbare monteurs (voor de
      // weekkop): werktijd per monteur minus bestaande afspraken.
      const getDayFreeCapacity = (dateStr) => {
        let free = 0;
        let anyone = false;
        // De organisatie-standaard kent geen weekend; in het weekend telt
        // alleen wie daar expliciet eigen werktijden voor heeft.
        const weekend = isoWeekday(dateStr) >= 6;
        for (const item of visibleInstallersForGrid.value) {
          if (item.id === 'unassigned') continue;
          const wt = techWorkTime(item.id, dateStr);
          if (wt.day_off) continue;
          if (weekend && wt.source !== 'member') continue;
          anyone = true;
          const s = parseHmToMinutes(wt.workday_start);
          const e = parseHmToMinutes(wt.workday_end);
          if (s == null || e == null) continue;
          const busy = busyIntervalsForDay(
            workOrders.value,
            dateStr,
            (wo) => matchesInstaller(wo, item.id),
            null
          );
          const lunch = wt.lunch_break;
          if (lunch) {
            const ls = parseHmToMinutes(lunch.start);
            const le = parseHmToMinutes(lunch.end);
            if (ls != null && le != null && le > ls) busy.push({ startMin: ls, endMin: le });
          }
          free += freeMinutesInWorkday(busy, s, e);
        }
        if (!anyone) return null;
        return { minutes: free, label: `${formatMinutes(free)} vrij` };
      };

      const weekDaysForView = computed(() => {
        const start = weekStartDate.value;
        const dayNames = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
        const today = new Date().toISOString().split('T')[0];
        return Array.from({ length: 7 }, (_, i) => {
          const d = new Date(start);
          d.setDate(d.getDate() + i);
          const dateStr = d.toISOString().split('T')[0];
          return {
            date: dateStr,
            dayName: dayNames[i],
            dateShort: d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }),
            isToday: dateStr === today,
          };
        });
      });

      const dayViewTitle = computed(() => {
        const d = new Date(selectedDayDate.value + 'T12:00:00');
        const date = d.toLocaleDateString('nl-NL', {
          weekday: 'long',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        return `${date} · week ${getISOWeek(d)}`;
      });

      const weekViewTitle = computed(() => {
        const start = weekStartDate.value;
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        return `Week ${getISOWeek(start)} · ${start.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      });

      const statusLegendItems = [
        { key: 'pending', label: 'In afwachting' },
        { key: 'scheduled', label: 'Ingepland' },
        { key: 'ready_for_installation', label: 'Klaar voor installatie' },
        { key: 'in_progress', label: 'Bezig' },
        { key: 'completed', label: 'Afgerond' },
        { key: 'cancelled', label: 'Geannuleerd' },
      ];

      const allStatusKeys = statusLegendItems.map((s) => s.key);
      const initVisibleStatusKeys = () => {
        const saved = loadSet(STORAGE_KEY_STATUSES);
        if (!saved) return new Set(allStatusKeys);
        const normalized = new Set([...saved].filter((key) => allStatusKeys.includes(key)));
        if (normalized.size !== saved.size) {
          saveSet(STORAGE_KEY_STATUSES, normalized);
        }
        return normalized;
      };
      const visibleStatusKeys = ref(initVisibleStatusKeys());
      const isStatusVisible = (key) => visibleStatusKeys.value.has(key);
      const toggleStatusFilter = (key) => {
        const next = new Set(visibleStatusKeys.value);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        visibleStatusKeys.value = next;
        saveSet(STORAGE_KEY_STATUSES, next);
      };

      const isInstallerVisible = (id) => !hiddenInstallerIds.value.has(id);

      const toggleInstaller = (id) => {
        const next = new Set(hiddenInstallerIds.value);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        hiddenInstallerIds.value = next;
        saveSet(STORAGE_KEY_HIDDEN, next);
        if (resourceConfig.value) {
          resourceConfig.value.resources.value = buildResources();
        }
      };

      const getWorkOrderCountForInstaller = (installerId) => {
        return plannedWorkOrders.value.filter((wo) => matchesInstaller(wo, installerId)).length;
      };

      // ── Filter dropdowns (Tippy) ──────────────────────────────────────────
      const monteursFilterRef = ref(null);
      const monteursFilterPanelRef = ref(null);
      const statusFilterRef = ref(null);
      const statusFilterPanelRef = ref(null);
      const workTypeFilterRef = ref(null);
      const workTypeFilterPanelRef = ref(null);
      const monteursFilterOpen = ref(false);
      const statusFilterOpen = ref(false);
      const workTypeFilterOpen = ref(false);
      let monteursFilterTippy = null;
      let statusFilterTippy = null;
      let workTypeFilterTippy = null;

      const monteursDropdownLabel = computed(() => {
        const total = installersWithUnassigned.value.length;
        const visible = installersWithUnassigned.value.filter((i) =>
          isInstallerVisible(i.id)
        ).length;
        return visible === total ? 'Monteurs' : `Monteurs (${visible}/${total})`;
      });

      const statusDropdownLabel = computed(() => {
        const total = statusLegendItems.length;
        const visible = statusLegendItems.filter((s) => isStatusVisible(s.key)).length;
        return visible === total ? 'Status' : `Status (${visible}/${total})`;
      });

      const workTypeDropdownLabel = computed(() => {
        const total = workTypeLegendItems.length;
        const visible = workTypeLegendItems.filter((t) => isWorkTypeVisible(t.key)).length;
        return visible === total ? 'Werktype' : `Werktype (${visible}/${total})`;
      });

      const showAllInstallers = () => {
        installersWithUnassigned.value.forEach((item) => {
          if (!isInstallerVisible(item.id)) toggleInstaller(item.id);
        });
      };
      const hideAllInstallers = () => {
        installersWithUnassigned.value.forEach((item) => {
          if (isInstallerVisible(item.id)) toggleInstaller(item.id);
        });
      };
      const showAllStatuses = () => {
        visibleStatusKeys.value = new Set(allStatusKeys);
        saveSet(STORAGE_KEY_STATUSES, visibleStatusKeys.value);
      };
      const hideAllStatuses = () => {
        visibleStatusKeys.value = new Set();
        saveSet(STORAGE_KEY_STATUSES, visibleStatusKeys.value);
      };

      const isWorkTypeVisible = (key) => visibleWorkTypeKeys.value.has(key);
      const toggleWorkTypeFilter = (key) => {
        const next = new Set(visibleWorkTypeKeys.value);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        visibleWorkTypeKeys.value = next;
        saveSet(STORAGE_KEY_PLANNING_WORK_TYPES, next);
      };
      const showAllWorkTypes = () => {
        visibleWorkTypeKeys.value = new Set(ALL_PLANNING_WORK_TYPE_KEYS);
        saveSet(STORAGE_KEY_PLANNING_WORK_TYPES, visibleWorkTypeKeys.value);
      };
      const hideAllWorkTypes = () => {
        visibleWorkTypeKeys.value = new Set();
        saveSet(STORAGE_KEY_PLANNING_WORK_TYPES, visibleWorkTypeKeys.value);
      };

      const getInitials = (label) => {
        return label
          .split(/\s+/)
          .map((w) => w[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
      };

      // Bepaal alle user-ids die aan deze werkbon gekoppeld zijn — zowel de
      // hoofdmonteur (`assigned_to`) als eventuele hulpmonteurs
      // (`assigned_users[]` of `assigned_user_ids[]`). Een werkbon hoort
      // in elke betreffende kolom getoond te worden, niet alleen bij de
      // primaire.
      const getAllAssigneeIds = (wo) => {
        const ids = new Set();
        const primary = wo.assigned_to?.id || wo.assigned_to;
        if (primary) ids.add(primary);
        (wo.assigned_users || []).forEach((u) => {
          const id = typeof u === 'string' ? u : u?.id;
          if (id) ids.add(id);
        });
        (wo.assigned_user_ids || []).forEach((id) => {
          if (id) ids.add(id);
        });
        return ids;
      };

      const matchesInstaller = (wo, installerId) => {
        const allIds = getAllAssigneeIds(wo);
        if (installerId === 'unassigned') return allIds.size === 0;
        const installer = installers.value.find(
          (i) => i.id === installerId || i.user?.id === installerId
        );
        const candidates = installer
          ? [installer.id, installer.user?.id].filter(Boolean)
          : [installerId];
        return candidates.some((c) => allIds.has(c));
      };

      // Voor de event-rendering: is deze monteur de hoofdmonteur of slechts
      // een hulpmonteur op deze werkbon? We tonen op de hulp-kaart een
      // subtielere stijl + "Hulp"-badge.
      const isHelperRole = (wo, installerId) => {
        if (installerId === 'unassigned') return false;
        const primary = wo.assigned_to?.id || wo.assigned_to || null;
        if (!primary) return false;
        const installer = installers.value.find(
          (i) => i.id === installerId || i.user?.id === installerId
        );
        const candidates = installer
          ? [installer.id, installer.user?.id].filter(Boolean)
          : [installerId];
        return !candidates.includes(primary);
      };

      const statusFilterFn = (wo) =>
        visibleStatusKeys.value.size === 0 || visibleStatusKeys.value.has(getDerivedState(wo));

      // ---- Materieelconflicten (warning-only, per werkbon in de zichtbare range) ----
      const resourceConflictsByWorkOrder = ref({});
      const resourceConflictCount = computed(
        () => Object.keys(resourceConflictsByWorkOrder.value).length
      );
      const resourceConflictLabel = (workOrderId) => {
        const list = resourceConflictsByWorkOrder.value[workOrderId];
        if (!list?.length) return '';
        const c = list[0];
        const name = c.resource_name || c.category || 'materieel';
        return `${name}: ${c.required} nodig, ${c.capacity} beschikbaar`;
      };
      const loadResourceConflicts = async () => {
        try {
          let from;
          let to;
          if (currentView.value === 'day') {
            from = selectedDayDate.value;
            to = selectedDayDate.value;
          } else {
            const start = new Date(weekStartDate.value);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);
            from = toLocalIsoDate(start);
            to = toLocalIsoDate(end);
          }
          const result = await resourceService.getConflicts(from, to);
          resourceConflictsByWorkOrder.value = result?.by_work_order || {};
        } catch (error) {
          resourceConflictsByWorkOrder.value = {};
        }
      };

      // Herbereken conflicten zodra een afspraak verschuift (drag/drop, verzetten, verwijderen).
      const scheduleSignature = computed(() =>
        workOrders.value.map((wo) => `${wo.id}:${wo.appointment_start || ''}`).join('|')
      );
      let conflictReloadTimer = null;
      watch(scheduleSignature, () => {
        clearTimeout(conflictReloadTimer);
        conflictReloadTimer = setTimeout(loadResourceConflicts, 400);
      });

      const decorateEvent = (wo, installerId, { withHeight = false } = {}) => {
        const start = new Date(getAppointmentStart(wo));
        const duration = wo.appointment_duration_minutes || 120;
        const end = new Date(start.getTime() + duration * 60000);
        const fmt = (d) => d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
        const event = {
          id: wo.id,
          workOrder: wo,
          timeRange: `${fmt(start)}-${fmt(end)}`,
          customer: wo.project?.lead?.name || 'Klant',
          address: wo.installation_address?.city || '',
          status: wo.status,
          isTentative: isTentativelyPlanned(wo),
          isConfirmed: isConfirmedForCard(wo),
          materials: getMaterialsState(wo),
          resourceConflict: resourceConflictLabel(wo.id),
          isUnassigned: getAllAssigneeIds(wo).size === 0,
          isHelper: isHelperRole(wo, installerId),
          palette: getStatusPalette(wo),
          revenueLabel:
            wo.planned_value_excl_vat != null && wo.planned_value_excl_vat > 0
              ? formatCurrency(wo.planned_value_excl_vat)
              : '',
        };
        if (withHeight) {
          // Hoogte berekenen: 24px per 15-min slot
          event.heightPx = Math.max(1, Math.ceil(duration / 15)) * 24;
        }
        return event;
      };

      // Vooraf berekende cel-indexen. Zonder deze memoisatie filterde élke cel
      // (dag: 49 slots × monteurs) bij elke re-render de volledige werkbon-
      // lijst opnieuw; tijdens het slepen (continu vurende dragover-events)
      // bevroor de app daardoor volledig.
      const dayCellMap = computed(() => {
        const map = new Map();
        const dateStr = selectedDayDate.value;
        const installerIds = visibleInstallersForGrid.value.map((i) => i.id);
        // Per kolom alle events van de dag, om overlappende kaarten naast
        // elkaar te zetten (lanes) in plaats van over elkaar heen.
        const perColumn = new Map(installerIds.map((id) => [id, []]));
        for (const wo of workOrders.value) {
          const appointmentDate = getAppointmentStart(wo);
          if (!appointmentDate) continue;
          const start = new Date(appointmentDate);
          if (start.toISOString().split('T')[0] !== dateStr) continue;
          if (!statusFilterFn(wo)) continue;
          // Alleen renderen in het START-slot: afronden naar het kwartier
          // waarin de afspraak begint.
          const startMin = start.getHours() * 60 + start.getMinutes();
          // Buiten het zichtbare venster? Dan in het eerste/laatste kwartier
          // tonen zodat de bon nooit onzichtbaar is.
          const { startMin: rangeStart, endMin: rangeEnd } = dayViewRange.value;
          const clampedMin = Math.min(Math.max(startMin, rangeStart), rangeEnd);
          const slotHour = Math.floor((clampedMin / 60) * 4) / 4;
          for (const installerId of installerIds) {
            if (!matchesInstaller(wo, installerId)) continue;
            const event = decorateEvent(wo, installerId, { withHeight: true });
            event.startMin = startMin;
            event.endMin = startMin + workOrderDuration(wo);
            event.lane = 0;
            event.laneCount = 1;
            perColumn.get(installerId).push(event);
            const key = `${installerId}|${slotHour}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(event);
          }
        }
        for (const events of perColumn.values()) {
          if (events.length < 2) continue;
          const lanes = assignLanes(events);
          for (const event of events) {
            const info = lanes.get(event.id);
            if (!info) continue;
            event.lane = info.lane;
            event.laneCount = info.laneCount;
          }
        }
        for (const events of map.values()) {
          events.sort((a, b) => a.timeRange.localeCompare(b.timeRange));
        }
        return map;
      });

      // Inline positionering voor overlappende dag-kaarten: kolom opdelen in
      // `laneCount` banen; één kaart = oude situatie (left/right 2px).
      const dayEventLaneStyle = (wo) => {
        if (!wo.laneCount || wo.laneCount < 2) return null;
        const width = `calc((100% - 4px) / ${wo.laneCount})`;
        return {
          left: `calc(2px + ${width} * ${wo.lane})`,
          width: `calc(${width} - 2px)`,
          right: 'auto',
        };
      };

      const weekCellMap = computed(() => {
        const map = new Map();
        const installerIds = visibleInstallersForGrid.value.map((i) => i.id);
        for (const wo of workOrders.value) {
          const appointmentDate = getAppointmentStart(wo);
          if (!appointmentDate) continue;
          if (!statusFilterFn(wo)) continue;
          const woDateStr = new Date(appointmentDate).toISOString().split('T')[0];
          for (const installerId of installerIds) {
            if (!matchesInstaller(wo, installerId)) continue;
            const key = `${installerId}|${woDateStr}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(decorateEvent(wo, installerId));
          }
        }
        for (const events of map.values()) {
          events.sort((a, b) => a.timeRange.localeCompare(b.timeRange));
        }
        return map;
      });

      const EMPTY_CELL = [];
      const getWorkOrdersForDayCell = (installerId, hour) =>
        dayCellMap.value.get(`${installerId}|${hour}`) || EMPTY_CELL;

      const getWorkOrdersForWeekCell = (installerId, dateStr) =>
        weekCellMap.value.get(`${installerId}|${dateStr}`) || EMPTY_CELL;

      const getWorkOrderCountForDay = (installerId) => {
        const dateStr = selectedDayDate.value;
        return workOrders.value.filter((wo) => {
          const appointmentDate = getAppointmentStart(wo);
          if (!appointmentDate) return false;
          const woDateStr = new Date(appointmentDate).toISOString().split('T')[0];
          return woDateStr === dateStr && matchesInstaller(wo, installerId);
        }).length;
      };

      const getWorkOrderCountForWeek = (installerId) => {
        return weekDaysForView.value.reduce((sum, day) => {
          return sum + getWorkOrdersForWeekCell(installerId, day.date).length;
        }, 0);
      };

      const navigateDay = (delta) => {
        const d = new Date(selectedDayDate.value + 'T12:00:00');
        d.setDate(d.getDate() + delta);
        selectedDayDate.value = d.toISOString().split('T')[0];
      };

      const navigateWeek = (delta) => {
        const d = new Date(weekStartDate.value);
        d.setDate(d.getDate() + delta * 7);
        weekStartDate.value = d;
      };

      const goToTodayView = () => {
        const today = new Date();
        selectedDayDate.value = today.toISOString().split('T')[0];
        weekStartDate.value = getMonday(today);
      };

      // Verslepen mag de bevestigingsstand niet wijzigen: een voorlopige bon
      // blijft voorlopig, een bevestigde blijft bevestigd (v2-invariant).
      // Een nog niet ingeplande bon start als voorlopig.
      // Behoud bestaande hulpmonteurs bij quick-schedule (drag/drop, klik):
      // we wijzigen alleen de hoofdmonteur en de afspraak, niet de helpers.
      const placementHelperIds = (workOrder, installerId, excludeHelperIds = []) => {
        const previousPrimaryId = workOrder?.assigned_to?.id || workOrder?.assigned_to || null;
        const previousAssigneeIds = new Set();
        (workOrder?.assigned_users || []).forEach((u) => {
          const id = typeof u === 'string' ? u : u?.id;
          if (id) previousAssigneeIds.add(id);
        });
        (workOrder?.assigned_user_ids || []).forEach((id) => {
          if (id) previousAssigneeIds.add(id);
        });
        if (previousPrimaryId) previousAssigneeIds.delete(previousPrimaryId);
        return [...previousAssigneeIds].filter(
          (id) => id !== installerId && !excludeHelperIds.includes(id)
        );
      };

      const buildSchedulePayload = (
        workOrder,
        isoDate,
        installerId,
        duration,
        excludeHelperIds = []
      ) => {
        const assignedUserIds = [
          ...(installerId ? [installerId] : []),
          ...placementHelperIds(workOrder, installerId, excludeHelperIds),
        ];

        return {
          status: statusAfterScheduling(workOrder),
          ...buildAppointmentPayload(workOrder, {
            startIso: isoDate,
            durationMinutes: duration,
            confirmed: isAppointmentConfirmed(workOrder),
          }),
          assigned_to: installerId,
          assigned_user_ids: assignedUserIds,
        };
      };

      // Houd de lokale `assigned_users` (objecten) in sync met het nieuwe
      // `assigned_user_ids`-array, anders blijft de oude (mogelijk nu ex-)
      // hulpmonteur via matchesInstaller in zijn kolom verschijnen.
      const applyScheduleResultLocal = (idx, payload) => {
        Object.assign(workOrders.value[idx], payload);
        // Sync ook het geneste `appointment`-object (v2-responses), anders
        // leest getAppointmentStart een verouderde waarde.
        applyAppointmentLocal(workOrders.value[idx], payload);
        if (Array.isArray(payload.assigned_user_ids)) {
          workOrders.value[idx].assigned_users = payload.assigned_user_ids.map((id) => ({ id }));
        }
      };

      // Optimistisch inplannen: de kaart verspringt direct naar het nieuwe
      // vak; accepteert de server het niet, dan zetten we de oude situatie
      // terug en tonen we een error-toast.
      const applyScheduleOptimistic = (workOrder, payload) => {
        const idx = workOrders.value.findIndex((wo) => wo.id === workOrder.id);
        if (idx === -1) return null;
        const snapshot = { ...workOrders.value[idx] };
        applyScheduleResultLocal(idx, payload);
        return snapshot;
      };

      const revertScheduleOptimistic = (workOrder, snapshot) => {
        if (!snapshot) return;
        const idx = workOrders.value.findIndex((wo) => wo.id === workOrder.id);
        if (idx !== -1) workOrders.value[idx] = snapshot;
      };

      // Lokale voorspelling van waar een bon landt (zelfde regels als de
      // server, inclusief werktijden per monteur): gebruikt voor de ghost en
      // voor de optimistische update.
      // Meewerkende collega's blijven op de bon staan, dus hun afspraken tellen
      // ook als bezet. Blokkeren alleen zij het slot, dan geeft de preview
      // `helpers_busy` terug zodat de planner kan kiezen (zonder hen / overlap).
      const previewPlacement = (
        workOrder,
        dateStr,
        installerId,
        strategy,
        requestedMin,
        { excludeHelperIds = [] } = {}
      ) => {
        const wt = techWorkTime(installerId, dateStr);
        if (wt.day_off && strategy !== PLACEMENT_STRATEGIES.EXACT) {
          return {
            ok: false,
            code: 'day_off',
            freeMinutes: 0,
            neededMinutes: workOrderDuration(workOrder),
          };
        }
        const busyFor = (techId) =>
          busyIntervalsForDay(
            workOrders.value,
            dateStr,
            (wo) => matchesInstaller(wo, techId),
            workOrder.id
          );
        const primaryId = installerId === 'unassigned' ? null : installerId;
        const primaryBusy = primaryId ? busyFor(primaryId) : [];
        const helperBusy = Object.fromEntries(
          placementHelperIds(workOrder, primaryId, excludeHelperIds).map((id) => [id, busyFor(id)])
        );
        const params = {
          strategy,
          requestedMin,
          durationMinutes: workOrderDuration(workOrder),
          workTime: wt,
        };
        const placement = placeInDay({
          ...params,
          busyIntervals: [...primaryBusy, ...Object.values(helperBusy).flat()],
        });
        if (placement.ok || !Object.keys(helperBusy).length) return placement;

        const { withoutHelpers, blockingHelperIds } = findBlockingHelpers({
          ...params,
          primaryBusy,
          helperBusy,
        });
        if (!withoutHelpers || !blockingHelperIds.length) return placement;
        return {
          ...placement,
          code: 'helpers_busy',
          withoutHelpers,
          blockingHelperIds,
          helperBusy,
        };
      };

      const placementFullMessage = (preview, label) => {
        if (preview.code === 'day_off') return `${label} is vrij op die dag`;
        return preview.freeMinutes > 0
          ? `${label} zit die dag vol — nog ${formatMinutes(preview.freeMinutes)} vrij, deze klus duurt ${formatMinutes(preview.neededMinutes)}`
          : `${label} zit die dag vol`;
      };

      // Strategie voor een drop op een dag (weekweergave): een bon die al een
      // tijd had, houdt die tijd (snapt door als bezet); een nieuwe bon komt
      // achteraan in de dag van die monteur.
      const dayDropStrategy = (workOrder) => {
        const existing = getAppointmentStart(workOrder);
        const minutes = existing ? appointmentLocalMinutes(existing) : null;
        if (minutes == null) return { strategy: PLACEMENT_STRATEGIES.APPEND, requestedMin: null };
        return { strategy: PLACEMENT_STRATEGIES.AT, requestedMin: minutes };
      };

      const placementErrorMessage = (err, installerLabel) => {
        const body = err?.response?.data || {};
        if (body.code === 'day_off') return `${installerLabel} is vrij op die dag`;
        if (body.code === 'day_full') {
          const free = Number(body.free_minutes) || 0;
          const needed = Number(body.needed_minutes) || 0;
          return free > 0
            ? `${installerLabel} zit die dag vol — nog ${formatMinutes(free)} vrij, deze klus duurt ${formatMinutes(needed)}`
            : `${installerLabel} zit die dag vol`;
        }
        if (body.code === 'technician_conflict') {
          const first = (body.conflicting_work_orders || [])[0];
          if (first) {
            const s = new Date(first.appointment_start);
            const e = new Date(first.appointment_end);
            const fmt = (d) =>
              d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
            return `${installerLabel} heeft dan al ${first.work_order_number || 'een afspraak'} (${fmt(s)}–${fmt(e)})`;
          }
          return `${installerLabel} heeft dan al een afspraak`;
        }
        if (body.code === 'work_order_not_schedulable')
          return body.message || 'Werkbon is niet meer te plannen';
        return 'Kon werkbon niet inplannen';
      };

      const installerLabelFor = (installerId) =>
        installersWithUnassigned.value.find((i) => i.id === installerId)?.label || 'Monteur';

      // Lokale preview en server-409 leveren dezelfde vorm voor de modal.
      const helpersBusyFromPreview = (preview) => ({
        blockers: preview.blockingHelperIds.map((helperId) => ({
          userId: helperId,
          name: getInstallerName(helperId),
          appointments: [...(preview.helperBusy[helperId] || [])]
            .sort((a, b) => a.startMin - b.startMin)
            .map(({ startMin, endMin, workOrder: wo }) => ({
              id: wo.id,
              number: wo.work_order_number || null,
              start: minutesToHm(startMin),
              end: minutesToHm(endMin),
              primaryName: wo.assigned_to ? getInstallerName(wo.assigned_to) : null,
            })),
        })),
        withoutHelpers: {
          start: minutesToHm(preview.withoutHelpers.startMin),
          end: minutesToHm(preview.withoutHelpers.endMin),
          moved: preview.withoutHelpers.moved,
        },
      });

      const helpersBusyFromServer = (body) => ({
        blockers: body.blockers.map((blocker) => ({
          userId: blocker.user_id,
          name: blocker.name || getInstallerName(blocker.user_id),
          appointments: (blocker.appointments || []).map((a) => ({
            id: a.work_order_id,
            number: a.work_order_number,
            start: a.start_local,
            end: a.end_local,
            primaryName: a.primary_technician_name,
          })),
        })),
        withoutHelpers: {
          start: body.without_helpers.start_local,
          end: body.without_helpers.end_local,
          moved: body.without_helpers.moved,
        },
      });

      const formatPlacementDate = (dateStr) =>
        new Date(`${dateStr}T12:00:00`).toLocaleDateString('nl-NL', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        });

      const openHelpersBusyModal = (placement, { blockers, withoutHelpers }) => {
        const { workOrder, dateStr, installerId, strategy, requestedMin } = placement;
        const helperIds = blockers.map((b) => b.userId);
        const helpers = blockers.map((b) => b.name).join(', ');
        const modalId = uiStore.addModal(PlanningHelpersBusyModal, {
          title: $t('planning_page.helpers_busy.title'),
          size: 'sm',
          technicianLabel: installerId === 'unassigned' ? null : installerLabelFor(installerId),
          dateLabel: formatPlacementDate(dateStr),
          blockers,
          withoutHelpers,
          onSubmit: async (choice) => {
            uiStore.closeModal(modalId);
            if (choice === 'without') {
              await placeWorkOrder(workOrder, dateStr, installerId, strategy, requestedMin, {
                excludeHelperIds: helperIds,
                successMessage: (start) =>
                  $t('planning_page.helpers_busy.toast_without', { time: start, helpers }),
              });
              return;
            }
            await placeWorkOrder(
              workOrder,
              dateStr,
              installerId,
              PLACEMENT_STRATEGIES.EXACT,
              parseHmToMinutes(withoutHelpers.start),
              {
                successMessage: (start) =>
                  $t('planning_page.helpers_busy.toast_overlap', { time: start, helpers }),
              }
            );
          },
        });
      };

      // Gezamenlijke plaatsing via de server (append / at / exact). Optimistisch
      // op het lokaal voorspelde slot; de server-waarde is leidend.
      const placeWorkOrder = async (
        workOrder,
        dateStr,
        installerId,
        strategy,
        requestedMin,
        { excludeHelperIds = [], successMessage = null } = {}
      ) => {
        const id = installerId === 'unassigned' ? null : installerId;
        const duration = workOrderDuration(workOrder);
        const label = installerLabelFor(installerId);
        const placement = { workOrder, dateStr, installerId, strategy, requestedMin };

        const preview = previewPlacement(workOrder, dateStr, installerId, strategy, requestedMin, {
          excludeHelperIds,
        });
        if (!preview.ok) {
          draggingWorkOrder.value = null;
          if (preview.code === 'helpers_busy') {
            openHelpersBusyModal(placement, helpersBusyFromPreview(preview));
            return;
          }
          toastStore.addToast({ type: 'error', message: placementFullMessage(preview, label) });
          return;
        }

        const previewIso = new Date(`${dateStr}T${minutesToHm(preview.startMin)}:00`).toISOString();
        const payload = buildSchedulePayload(workOrder, previewIso, id, duration, excludeHelperIds);
        const snapshot = applyScheduleOptimistic(workOrder, payload);
        try {
          const result = await workOrderService.placeWorkOrder(workOrder.id, {
            date: dateStr,
            technician_id: id,
            time: requestedMin != null ? minutesToHm(requestedMin) : undefined,
            strategy,
            ...(excludeHelperIds.length ? { exclude_helper_ids: excludeHelperIds } : {}),
          });
          const placed = result?.placed || {};
          const serverWo = result?.work_order || {};
          const idx = workOrders.value.findIndex((wo) => wo.id === workOrder.id);
          if (idx !== -1) {
            // Server-waarden zijn leidend; de kaart-relaties (project, lead)
            // blijven lokaal, de place-response bevat die niet.
            const merged = {
              ...payload,
              appointment_start: placed.start || payload.appointment_start,
              appointment_duration_minutes:
                serverWo.appointment_duration_minutes || payload.appointment_duration_minutes,
              appointment_confirmed_at:
                serverWo.appointment_confirmed_at ?? payload.appointment_confirmed_at,
              assigned_to: serverWo.assigned_to ?? payload.assigned_to,
              assigned_user_ids: serverWo.assigned_user_ids || payload.assigned_user_ids,
              status: serverWo.status || payload.status,
            };
            if (serverWo.updated_at) merged.updated_at = serverWo.updated_at;
            applyScheduleResultLocal(idx, merged);
          }

          const codes = (placed.warnings || []).map((w) => w.code);
          if (successMessage && placed.start_local) {
            toastStore.addToast({ type: 'success', message: successMessage(placed.start_local) });
          } else if (placed.moved && requestedMin != null) {
            toastStore.addToast({
              type: 'info',
              message: `Ingepland om ${placed.start_local} — ${minutesToHm(requestedMin)} was bezet bij ${label}`,
            });
          } else if (codes.includes('technician_conflict')) {
            toastStore.addToast({
              type: 'info',
              message: `Ingepland om ${placed.start_local} met overlap (bewust, via ⌥)`,
            });
          } else if (codes.includes('outside_working_hours')) {
            toastStore.addToast({
              type: 'info',
              message: `Ingepland om ${placed.start_local} — buiten werktijd (${workTime.value.workday_start}–${workTime.value.workday_end})`,
            });
          } else {
            toastStore.addToast({
              type: 'success',
              message: placed.start_local
                ? `Ingepland om ${placed.start_local}`
                : 'Werkbon ingepland',
            });
          }
        } catch (err) {
          console.error(err);
          revertScheduleOptimistic(workOrder, snapshot);
          const body = err?.response?.data || {};
          if (body.blockers?.length && body.without_helpers) {
            openHelpersBusyModal(placement, helpersBusyFromServer(body));
          } else {
            toastStore.addToast({ type: 'error', message: placementErrorMessage(err, label) });
          }
        }
        draggingWorkOrder.value = null;
      };

      // Drop op een kwartier (dagweergave): die tijd, snapt door als bezet;
      // met ⌥/Alt ingedrukt exact op de droptijd (overlap toegestaan).
      const scheduleWorkOrderAt = (workOrder, dateStr, hour, installerId, { exact = false } = {}) =>
        placeWorkOrder(
          workOrder,
          dateStr,
          installerId,
          exact ? PLACEMENT_STRATEGIES.EXACT : PLACEMENT_STRATEGIES.AT,
          Math.round(hour * 60)
        );

      // Drop op een dag (weekweergave): achteraan bij de monteur, of de eigen
      // tijd behouden als de bon al een afspraak had.
      const scheduleWorkOrderAtDate = (workOrder, dateStr, installerId) => {
        const { strategy, requestedMin } = dayDropStrategy(workOrder);
        return placeWorkOrder(workOrder, dateStr, installerId, strategy, requestedMin);
      };

      const pendingSlot = ref(null);

      const onDayCellClick = (hour, installerId) => {
        if (draggingWorkOrder.value) {
          scheduleWorkOrderAt(draggingWorkOrder.value, selectedDayDate.value, hour, installerId);
          return;
        }
        pendingSlot.value = {
          type: 'day',
          date: selectedDayDate.value,
          hour,
          installerId,
        };
        openPanelForPick();
      };

      // Ghost-preview tijdens het slepen: de cel waar de muis nu boven hangt.
      const dragOverSlot = ref(null);

      // Tijdens het slepen verbergen we de originele kaart, zodat het lijkt
      // alsof de kaart zelf van vakje naar vakje springt (de ghost is dan de
      // enige zichtbare versie).
      const dragSourceHiddenId = ref(null);

      const clearDragState = () => {
        draggingWorkOrder.value = null;
        dragOverSlot.value = null;
        dragSourceHiddenId.value = null;
      };

      // Ghost toont waar de bon écht landt (zelfde regels als de server):
      //  - ok:       op de muispositie / achteraan in de dag
      //  - moved:    verschoven naar het eerste vrije gat ("09:00 → 10:15")
      //  - outside:  buiten werktijd (toegestaan, oranje)
      //  - exact:    ⌥ ingedrukt, exact op droptijd met overlap (rood)
      //  - full:     past niet meer in de dag (rood, drop doet niets)
      const dragGhost = computed(() => {
        const wo = draggingWorkOrder.value;
        const slot = dragOverSlot.value;
        if (!wo || !slot) return null;

        const duration = workOrderDuration(wo);
        const dateStr = slot.type === 'day' ? selectedDayDate.value : slot.date;
        let strategy;
        let requestedMin;
        if (slot.type === 'day') {
          requestedMin = Math.round(slot.hour * 60);
          strategy = slot.exact ? PLACEMENT_STRATEGIES.EXACT : PLACEMENT_STRATEGIES.AT;
        } else {
          ({ strategy, requestedMin } = dayDropStrategy(wo));
        }

        const placement = previewPlacement(wo, dateStr, slot.installerId, strategy, requestedMin);
        const base = {
          ...slot,
          customer: wo.project?.lead?.name || 'Klant',
          heightPx: Math.max(1, Math.ceil(duration / 15)) * 24,
          palette: getStatusPalette(wo),
        };

        if (placement.code === 'helpers_busy') {
          return {
            ...base,
            state: 'helpers',
            renderHour: Math.floor((placement.withoutHelpers.startMin / 60) * 4) / 4,
            timeRange: $t('planning_page.helpers_busy.ghost'),
          };
        }

        if (!placement.ok) {
          return {
            ...base,
            state: 'full',
            renderHour: slot.hour,
            timeRange:
              placement.code === 'day_off'
                ? 'Vrije dag — ⌥ voor exact'
                : placement.freeMinutes > 0
                  ? `Vol — nog ${formatMinutes(placement.freeMinutes)} vrij`
                  : 'Dag zit vol',
          };
        }

        const startHm = minutesToHm(placement.startMin);
        const endHm = minutesToHm(placement.endMin);
        let state = 'ok';
        let timeRange = `${startHm}-${endHm}`;
        if (placement.warnings.includes('technician_conflict')) {
          state = 'exact';
          timeRange = `${startHm}-${endHm} · overlap`;
        } else if (placement.moved && requestedMin != null) {
          state = 'moved';
          timeRange = `${minutesToHm(requestedMin)} → ${startHm}-${endHm}`;
        } else if (placement.warnings.includes('outside_working_hours')) {
          state = 'outside';
          timeRange = `${startHm}-${endHm} · buiten werktijd`;
        }

        return {
          ...base,
          state,
          timeRange,
          // Dagweergave: ghost in de cel van het berekende startkwartier.
          renderHour: Math.floor((placement.startMin / 60) * 4) / 4,
        };
      });

      // Alleen wissen als de drag het kalender-grid echt verlaat; tussen
      // cellen onderling overschrijft de volgende dragover de slot-waarde.
      const onCalendarDragLeave = (e) => {
        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
        dragOverSlot.value = null;
      };

      const onDayCellDragOver = (e, hour, installerId) => {
        e.dataTransfer.dropEffect = 'move';
        // ⌥/Alt tijdens het slepen = exact op de droptijd (overlap toegestaan).
        const exact = !!e.altKey;
        // dragover vuurt continu (ook bij stilstaande muis); alleen een nieuw
        // object toekennen als de cel (of de ⌥-stand) echt wisselt, anders
        // re-rendert de hele kalender tientallen keren per seconde.
        const cur = dragOverSlot.value;
        if (
          cur?.type === 'day' &&
          cur.hour === hour &&
          cur.installerId === installerId &&
          cur.exact === exact
        ) {
          return;
        }
        dragOverSlot.value = { type: 'day', hour, installerId, exact };
      };

      const onDayCellDrop = (e, hour, installerId) => {
        const woId = e.dataTransfer.getData('workOrderId');
        const exact = !!e.altKey || !!dragOverSlot.value?.exact;
        // Drag-state direct opruimen (niet pas na de PATCH): de ghost en
        // cel-highlights mogen niet blijven hangen terwijl het opslaan loopt.
        clearDragState();
        const wo = workOrders.value.find((w) => w.id === woId);
        if (wo) scheduleWorkOrderAt(wo, selectedDayDate.value, hour, installerId, { exact });
      };

      const onWeekCellClick = (dateStr, installerId) => {
        if (draggingWorkOrder.value) {
          scheduleWorkOrderAtDate(draggingWorkOrder.value, dateStr, installerId);
          return;
        }
        pendingSlot.value = {
          type: 'week',
          date: dateStr,
          installerId,
        };
        openPanelForPick();
      };

      const onWeekCellDragOver = (e, dateStr, installerId) => {
        e.dataTransfer.dropEffect = 'move';
        const cur = dragOverSlot.value;
        if (cur?.type === 'week' && cur.date === dateStr && cur.installerId === installerId) return;
        dragOverSlot.value = { type: 'week', date: dateStr, installerId };
      };

      const onWeekCellDrop = (e, dateStr, installerId) => {
        const woId = e.dataTransfer.getData('workOrderId');
        clearDragState();
        const wo = workOrders.value.find((w) => w.id === woId);
        if (wo) scheduleWorkOrderAtDate(wo, dateStr, installerId);
      };

      const closeUnplannedDrawer = () => {
        const wasPickMode = !!pendingSlot.value;
        if (wasPickMode) {
          // Sluiten tijdens een pick = pick annuleren; het paneel blijft
          // staan als het al open stond vóór de klik op de cel.
          finishPick();
          return;
        }
        setPanelOpen(false);
        clearWorkOrderSelection();
      };

      // Geplande kaart op het paneel laten vallen: afspraak wissen, bon terug
      // naar "Te plannen". Monteur(s) blijven staan, net als de duur.
      const isDraggingPlannedWorkOrder = computed(
        () => !!draggingWorkOrder.value && isPlanned(draggingWorkOrder.value)
      );

      const handleUnscheduleDrop = async (workOrderId) => {
        clearDragState();
        const workOrder = workOrders.value.find((w) => w.id === workOrderId);
        if (!workOrder || !isPlanned(workOrder)) return;

        const payload = {
          status: statusAfterScheduling(workOrder),
          appointment_start: null,
          appointment_confirmed_at: null,
          appointment_duration_minutes: workOrder.appointment_duration_minutes || 120,
        };
        const snapshot = applyScheduleOptimistic(workOrder, payload);
        try {
          await workOrderService.updateWorkOrder(workOrder.id, payload);
          toastStore.addToast({
            message: 'Afspraak gewist — werkbon staat weer bij Te plannen',
            type: 'success',
          });
        } catch (err) {
          console.error(err);
          revertScheduleOptimistic(workOrder, snapshot);
          toastStore.addToast({ message: 'Kon afspraak niet wissen', type: 'error' });
        }
      };

      const pendingSlotLabel = computed(() => {
        if (!pendingSlot.value) return '';
        const d = new Date(pendingSlot.value.date + 'T12:00:00');
        const dateStr = d.toLocaleDateString('nl-NL', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        });
        const installer = installersWithUnassigned.value.find(
          (i) => i.id === pendingSlot.value.installerId
        );
        const name = installer?.label || '';
        if (pendingSlot.value.hour != null) {
          const totalMin = Math.round(pendingSlot.value.hour * 60);
          const h = Math.floor(totalMin / 60);
          const m = totalMin % 60;
          const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          return `${dateStr} ${time} — ${name}`;
        }
        return `${dateStr} — ${name}`;
      });

      const pendingSlotDate = computed(() => pendingSlot.value?.date || '');

      // --- Klantvenster-feedback in de kalender ---

      // Tijdens slepen: kleur cellen naar het venster van de gesleepte werkbon.
      // Weekcellen kijken alleen naar de dag; dagcellen ook naar het dagdeel.
      const dragWindowCellClass = (dateStr, hour, base) => {
        const wo = draggingWorkOrder.value;
        if (!wo) return null;
        const window = getWorkOrderWindow(wo);
        if (!window) return null;
        const startMinutes = hour == null ? null : Math.round(hour * 60);
        const verdict = evaluatePlanningWindow(window, dateStr, startMinutes);
        if (verdict.match === 'inside') return `${base}--window-ok`;
        if (verdict.match === 'outside') {
          return verdict.hard ? `${base}--window-blocked` : `${base}--window-warn`;
        }
        return null;
      };

      const dragWindowCellTitle = (dateStr) => {
        const wo = draggingWorkOrder.value;
        if (!wo) return undefined;
        const window = getWorkOrderWindow(wo);
        if (!window) return undefined;
        const verdict = evaluatePlanningWindow(window, dateStr);
        if (verdict.match === 'outside') {
          return verdict.hard
            ? `Klant kan hier niet: ${verdict.reason}`
            : `Buiten voorkeur klant (${verdict.reason}) — eerst bellen`;
        }
        if (verdict.match === 'inside')
          return `Past bij voorkeur klant (${formatPlanningWindow(window)})`;
        return undefined;
      };

      // Per weekdag: hoeveel ongeplande bonnen kunnen alleen / liefst op die dag.
      const windowDemandByWeekday = computed(() => {
        const map = new Map();
        for (const wo of unplannedWorkOrders.value) {
          const window = getWorkOrderWindow(wo);
          if (!window || window.mode === 'any' || !(window.weekdays || []).length) continue;
          for (const day of window.weekdays) {
            if (!map.has(day)) map.set(day, { only: [], prefer: [] });
            map.get(day)[window.mode === 'only' ? 'only' : 'prefer'].push(wo);
          }
        }
        return map;
      });

      const WEEKDAY_FULL = [
        '',
        'maandag',
        'dinsdag',
        'woensdag',
        'donderdag',
        'vrijdag',
        'zaterdag',
        'zondag',
      ];

      const topCities = (list) => {
        const counts = new Map();
        for (const wo of list) {
          const city = getCityGroup(wo)?.label;
          if (!city) continue;
          counts.set(city, (counts.get(city) || 0) + 1);
        }
        return [...counts.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 2)
          .map(([city, n]) => `${n} in ${city}`)
          .join(', ');
      };

      const getWindowDemand = (dateStr) => {
        const weekday = isoWeekday(dateStr);
        const demand = windowDemandByWeekday.value.get(weekday);
        if (!demand) return null;
        // Respect not_before / not_after for this specific date.
        const only = demand.only.filter(
          (wo) => evaluatePlanningWindow(getWorkOrderWindow(wo), dateStr).match !== 'outside'
        );
        const prefer = demand.prefer.filter(
          (wo) => evaluatePlanningWindow(getWorkOrderWindow(wo), dateStr).match !== 'outside'
        );
        if (!only.length && !prefer.length) return null;

        const parts = [];
        if (only.length) parts.push(`${only.length} alleen`);
        if (prefer.length) parts.push(`${prefer.length} liefst`);

        const dayName = WEEKDAY_FULL[weekday];
        const titleParts = [];
        if (only.length) {
          const cities = topCities(only);
          titleParts.push(
            `${only.length} ${only.length === 1 ? 'bon kan' : 'bonnen kunnen'} alleen op ${dayName}${cities ? ` (${cities})` : ''}`
          );
        }
        if (prefer.length) {
          const cities = topCities(prefer);
          titleParts.push(
            `${prefer.length} ${prefer.length === 1 ? 'bon heeft' : 'bonnen hebben'} voorkeur voor ${dayName}${cities ? ` (${cities})` : ''}`
          );
        }

        return {
          onlyCount: only.length,
          preferCount: prefer.length,
          label: parts.join(' · '),
          title: titleParts.join('\n'),
        };
      };

      const handlePickWorkOrder = async (wo) => {
        if (!pendingSlot.value) return;
        const slot = pendingSlot.value;
        if (slot.type === 'day' && slot.hour != null) {
          await scheduleWorkOrderAt(wo, slot.date, slot.hour, slot.installerId);
        } else {
          await scheduleWorkOrderAtDate(wo, slot.date, slot.installerId);
        }
        finishPick();
      };

      // Unplanned work orders (geen afspraak)
      const unplannedWorkOrders = computed(() => {
        return workOrders.value.filter((wo) => !isPlanned(wo));
      });

      // Voorlopig ingeplande werkbonnen - wel afspraak, nog niet bevestigd
      const tentativeWorkOrders = computed(() => {
        return workOrders.value.filter((wo) => isTentativelyPlanned(wo));
      });

      const matchesWorkType = (wo) => {
        const type = wo.work_order_type || 'installation';
        return visibleWorkTypeKeys.value.size === 0 || visibleWorkTypeKeys.value.has(type);
      };

      const getWorkOrderCountForWorkType = (typeKey) => {
        const matchType = (wo) => (wo.work_order_type || 'installation') === typeKey;
        return (
          unplannedWorkOrders.value.filter(matchType).length +
          tentativeWorkOrders.value.filter(matchType).length
        );
      };

      const filteredUnplannedForDrawer = computed(() =>
        unplannedWorkOrders.value.filter(matchesWorkType)
      );

      const filteredTentativeForDrawer = computed(() =>
        tentativeWorkOrders.value.filter(matchesWorkType)
      );

      const planningQueueOpenCount = computed(() => filteredUnplannedForDrawer.value.length);
      const planningQueueTentativeCount = computed(() => filteredTentativeForDrawer.value.length);
      const planningQueueCount = computed(
        () => planningQueueOpenCount.value + planningQueueTentativeCount.value
      );
      const planningQueueButtonLabel = computed(() => {
        if (planningQueueCount.value === 0) return 'Te plannen';
        const open = planningQueueOpenCount.value;
        const tentative = planningQueueTentativeCount.value;
        if (open > 0 && tentative > 0) {
          return `Te plannen (${open} + ${tentative})`;
        }
        return `Te plannen (${planningQueueCount.value})`;
      });
      const planningQueueTooltip = computed(() => {
        if (planningQueueCount.value === 0) return 'Geen open werkbonnen voor planning';
        const parts = [];
        if (planningQueueOpenCount.value > 0) {
          parts.push(`${planningQueueOpenCount.value} zonder datum`);
        }
        if (planningQueueTentativeCount.value > 0) {
          parts.push(`${planningQueueTentativeCount.value} voorlopig`);
        }
        return parts.join(', ');
      });

      const weekStartIso = computed(() => {
        const d = weekStartDate.value;
        if (typeof d === 'string') return d;
        return d.toISOString().split('T')[0];
      });

      const visibleTechnicianUserIds = computed(() =>
        installers.value
          .map((i) => getInstallerUserId(i))
          .filter((id) => !hiddenInstallerIds.value.has(id))
      );

      const selectionSummary = computed(() => {
        const selected = workOrders.value.filter((wo) => selectedWorkOrderIds.value.has(wo.id));
        const cities = new Set(selected.map((wo) => getCityGroup(wo).label));
        return {
          totalDuration: selected.reduce(
            (sum, wo) => sum + (wo.appointment_duration_minutes || 120),
            0
          ),
          tentativeCount: selected.filter((wo) => isTentativelyPlanned(wo)).length,
          missingDurationCount: selected.filter((wo) => !wo.appointment_duration_minutes).length,
          missingAddressCount: selected.filter((wo) => {
            const addr = wo.installation_address || wo.project?.installation_address;
            return !(addr?.street && addr?.city);
          }).length,
          clusterCity: cities.size === 1 ? [...cities][0] : null,
        };
      });

      const toggleWorkOrderSelection = (id) => {
        const next = new Set(selectedWorkOrderIds.value);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        selectedWorkOrderIds.value = next;
      };

      const clearWorkOrderSelection = () => {
        selectedWorkOrderIds.value = new Set();
      };

      const setWorkOrderSelection = (ids = []) => {
        selectedWorkOrderIds.value = new Set(ids);
      };

      const addWorkOrderSelection = (ids = []) => {
        const next = new Set(selectedWorkOrderIds.value);
        ids.forEach((id) => next.add(id));
        selectedWorkOrderIds.value = next;
      };

      const removeWorkOrderSelection = (ids = []) => {
        const next = new Set(selectedWorkOrderIds.value);
        ids.forEach((id) => next.delete(id));
        selectedWorkOrderIds.value = next;
      };

      const getSelectedWorkOrderIds = () => [...selectedWorkOrderIds.value];

      const { confirmDeleteWorkOrders } = useWorkOrderDelete();

      const removeWorkOrdersLocally = (ids) => {
        workOrders.value = workOrders.value.filter((wo) => !ids.includes(wo.id));
        const next = new Set(selectedWorkOrderIds.value);
        ids.forEach((id) => next.delete(id));
        selectedWorkOrderIds.value = next;
      };

      const handleDeleteWorkOrder = (workOrder) => {
        confirmDeleteWorkOrders(workOrder, {
          onDeleted: (ids) => removeWorkOrdersLocally(ids),
        });
      };

      const handleBulkDelete = () => {
        const selected = workOrders.value.filter((wo) => selectedWorkOrderIds.value.has(wo.id));
        confirmDeleteWorkOrders(selected, {
          onDeleted: (ids) => removeWorkOrdersLocally(ids),
        });
      };

      const handleOpenBulkPlanModal = () => {
        if (!selectedWorkOrderIds.value.size) return;

        const modalId = uiStore.addModal(PlanningBulkPlanModal, {
          title: 'Planningsvoorstel',
          size: 'sm',
          selectedCount: selectedWorkOrderIds.value.size,
          selectionSummary: selectionSummary.value,
          installerOptions: installerOptions.value,
          weekStartDefault: weekStartIso.value,
          onSubmit: (payload) => {
            uiStore.closeModal(modalId);
            handlePreviewPlan(payload);
          },
        });
      };

      const handlePreviewPlan = async (formData) => {
        const ids = getSelectedWorkOrderIds();
        if (!ids.length) return;

        const modalId = uiStore.addModal(RoutePlanningModal, {
          title: 'Route wordt gepland',
          size: 'xl',
          closable: false,
          bodyScroll: false,
          loading: true,
          applying: false,
          preview: null,
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
          onApply: async () => {
            const modal = uiStore.modals.find((m) => m.id === modalId);
            const preview = modal?.props?.preview;
            if (!preview?.plan_id) return;

            uiStore.updateModalProps(modalId, { applying: true });
            try {
              const result = await workOrderService.applyRouteOptimization(preview.plan_id);
              const updatedIds =
                result.updated_work_order_ids ||
                preview.assignments?.map((a) => a.work_order_id) ||
                [];
              await refreshWorkOrders();
              uiStore.closeModal(modalId);
              clearWorkOrderSelection();
              toastStore.addToast({
                message: `${updatedIds.length || 'Alle'} werkbon(nen) ingepland`,
                type: 'success',
              });
            } catch (error) {
              console.error(error);
              toastStore.addToast({
                message: 'Kon voorstel niet toepassen',
                type: 'error',
              });
            } finally {
              uiStore.updateModalProps(modalId, { applying: false });
            }
          },
        });

        const startedAt = Date.now();
        try {
          const result = await workOrderService.previewRouteOptimization({
            work_order_ids: ids,
            ...formData,
          });
          const elapsed = Date.now() - startedAt;
          if (elapsed < ROUTE_PLANNING_MIN_LOADING_MS) {
            await new Promise((resolve) =>
              setTimeout(resolve, ROUTE_PLANNING_MIN_LOADING_MS - elapsed)
            );
          }
          uiStore.updateModalProps(modalId, {
            loading: false,
            preview: { ...result, planning_type: formData.planning_type },
            title: 'Voorstel bekijken',
          });
        } catch (error) {
          console.error(error);
          uiStore.closeModal(modalId);
          toastStore.addToast({
            message: 'Kon planning niet berekenen',
            type: 'error',
          });
        }
      };

      const handleBulkConfirm = async () => {
        const ids = getSelectedWorkOrderIds().filter((id) => {
          const wo = workOrders.value.find((w) => w.id === id);
          return wo && isTentativelyPlanned(wo);
        });
        if (!ids.length) {
          toastStore.addToast({
            message: 'Geen voorlopige werkbonnen geselecteerd',
            type: 'info',
          });
          return;
        }
        bulkLoading.value = true;
        bulkActiveAction.value = 'confirm';
        try {
          const result = await workOrderService.bulkConfirmWorkOrders({
            work_order_ids: ids,
          });
          const confirmed = result.confirmed || [];
          confirmed.forEach((item) => {
            const idx = workOrders.value.findIndex((wo) => wo.id === item.work_order_id);
            if (idx !== -1) {
              const wo = workOrders.value[idx];
              const confirmedAt = item.appointment_confirmed_at || new Date().toISOString();
              wo.derived_state = undefined;
              wo.appointment_confirmed_at = confirmedAt;
              if (item.appointment_confirmed_by) {
                wo.appointment_confirmed_by = item.appointment_confirmed_by;
              }
              if (wo.appointment) {
                wo.appointment = { ...wo.appointment, confirmed_at: confirmedAt };
              }
            }
          });
          const failed = result.failed || [];
          if (confirmed.length) {
            toastStore.addToast({
              message: `${confirmed.length} werkbon(nen) bevestigd`,
              type: 'success',
            });
          }
          if (failed.length) {
            toastStore.addToast({
              message: `${failed.length} werkbon(nen) mislukt`,
              type: 'error',
            });
          }
          clearWorkOrderSelection();
        } catch (error) {
          console.error(error);
          toastStore.addToast({
            message: 'Bulk bevestigen mislukt',
            type: 'error',
          });
        } finally {
          bulkLoading.value = false;
          bulkActiveAction.value = null;
        }
      };

      // Planned work orders (voorlopig of bevestigd) - for calendar display
      const plannedWorkOrders = computed(() => {
        return workOrders.value.filter((wo) => isPlanned(wo));
      });

      const getStatusLabel = (status) => {
        const labels = {
          pending: 'In afwachting',
          scheduled: 'Ingepland',
          ready_for_installation: 'Klaar voor installatie',
          in_progress: 'Bezig',
          completed: 'Voltooid',
          cancelled: 'Geannuleerd',
        };
        return labels[status] || status;
      };

      const getInstallerName = (assignedTo) => {
        if (!assignedTo) return 'Niet toegewezen';
        if (typeof assignedTo === 'object') {
          const name = `${assignedTo.first_name || ''} ${assignedTo.last_name || ''}`.trim();
          return name || assignedTo.email || 'Onbekend';
        }
        // Find by ID (membership id or user id)
        const installer = installers.value.find(
          (i) => i.id === assignedTo || i.user?.id === assignedTo
        );
        return installer ? getMemberDisplayName(installer) : 'Onbekend';
      };

      const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('nl-NL', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(date);
      };

      const updateCalendarEvents = () => {
        // No-op: custom grids use workOrders reactively
      };

      const switchView = (view) => {
        currentView.value = view;
        localStorage.setItem(STORAGE_KEY_VIEW, view);
      };

      // Load work orders
      const loadWorkOrdersForRange = async (startDate, endDate) => {
        const monthKey = startDate.substring(0, 7);
        if (loadedRanges.value.has(monthKey)) return;

        try {
          loading.value = true;
          loadedRanges.value.add(monthKey);

          const response = await workOrderService.getAllWorkOrdersPaginated({
            appointment_date_from: startDate,
            appointment_date_to: endDate,
          });
          const data = response.workOrders || [];

          // Merge new work orders
          const existingIds = new Set(workOrders.value.map((wo) => wo.id));
          const newWorkOrders = data.filter((wo) => !existingIds.has(wo.id));
          workOrders.value = [...workOrders.value, ...newWorkOrders];

          updateCalendarEvents();
        } catch (error) {
          console.error('Error loading work orders:', error);
          loadedRanges.value.delete(monthKey);
        } finally {
          loading.value = false;
        }
      };

      // Helper: load the calendar month that contains `date`. Accepts
      // either a Date object or a `YYYY-MM-DD` string. The actual
      // network call is gated by `loadedRanges` so calling this for
      // a month we already have is cheap.
      const loadMonthForDate = (date) => {
        const d = typeof date === 'string' ? new Date(`${date}T12:00:00`) : new Date(date);
        if (Number.isNaN(d.getTime())) return Promise.resolve();
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        return loadWorkOrdersForRange(startDate, endDate);
      };

      // Load whatever month(s) the *current* view needs. Day view is
      // always one month; week view can straddle two months when the
      // Monday and the Sunday fall on either side of a month boundary,
      // so we load both in that case.
      const loadMonthsForCurrentView = async () => {
        if (currentView.value === 'day') {
          await loadMonthForDate(selectedDayDate.value);
          return;
        }
        const start = new Date(weekStartDate.value);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        await loadMonthForDate(start);
        if (end.getFullYear() !== start.getFullYear() || end.getMonth() !== start.getMonth()) {
          await loadMonthForDate(end);
        }
      };

      const appointmentIsoDate = (wo) => {
        const start = getAppointmentStart(wo);
        if (!start) return null;
        const d = start instanceof Date ? start : new Date(start);
        if (Number.isNaN(d.getTime())) return null;
        return toLocalIsoDate(d);
      };

      const plannedValueOf = (wo) => {
        if (!wo || wo.status === 'cancelled') return 0;
        const type = wo.work_order_type || 'installation';
        if (type !== 'installation') return 0;
        return Number(wo.planned_value_excl_vat) || 0;
      };

      const sumPlannedInRange = (from, to) =>
        workOrders.value.reduce((sum, wo) => {
          const day = appointmentIsoDate(wo);
          if (!day || day < from || day > to) return sum;
          return sum + plannedValueOf(wo);
        }, 0);

      const plannedViewValue = computed(() => {
        const { from, to } = plannedRange.value;
        return sumPlannedInRange(from, to);
      });
      const plannedMonthValue = computed(() =>
        sumPlannedInRange(monthRange.value.from, monthRange.value.to)
      );
      const plannedAvgPerDay = computed(() => {
        const days = monthRange.value.days || 1;
        return plannedMonthValue.value / days;
      });
      const plannedPeriodLabel = computed(() => {
        if (currentView.value === 'day') return dayViewTitle.value;
        return weekViewTitle.value;
      });
      const monthPeriodLabel = computed(() => {
        const d = new Date(`${monthRange.value.from}T12:00:00`);
        const label = d.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
        return label.charAt(0).toUpperCase() + label.slice(1);
      });

      const buildRevenuePayload = (from, to) => {
        const items = [];
        const totals = {
          planned_confirmed: 0,
          planned_tentative: 0,
          unplanned: 0,
          realized: 0,
        };
        for (const wo of workOrders.value) {
          const value = plannedValueOf(wo);
          const type = wo.work_order_type || 'installation';
          if (type !== 'installation' || wo.status === 'cancelled') continue;

          const start = getAppointmentStart(wo);
          const startDay = appointmentIsoDate(wo);
          const completedDay = wo.completed_at ? toLocalIsoDate(new Date(wo.completed_at)) : null;
          const inAppt = startDay && startDay >= from && startDay <= to;
          const inCompleted = completedDay && completedDay >= from && completedDay <= to;
          const isOpen = wo.status !== 'completed';
          const buckets = [];
          if (!start && isOpen) buckets.push('unplanned');
          if (inAppt) buckets.push(isAppointmentConfirmed(wo) ? 'confirmed' : 'tentative');
          if (inCompleted) buckets.push('realized');
          if (buckets.length === 0) continue;

          if (buckets.includes('confirmed')) totals.planned_confirmed += value;
          if (buckets.includes('tentative')) totals.planned_tentative += value;
          if (buckets.includes('unplanned')) totals.unplanned += value;
          if (buckets.includes('realized')) totals.realized += value;

          items.push({
            work_order_id: wo.id,
            work_order_number: wo.work_order_number,
            customer_name: wo.project?.lead?.name || null,
            project_number: wo.project?.project_number || null,
            value_excl_vat: value,
            appointment_start: start,
            appointment_confirmed_at: wo.appointment_confirmed_at,
            completed_at: wo.completed_at,
            status: wo.status,
            buckets,
          });
        }
        return {
          summary: {
            ...totals,
            planned: totals.planned_confirmed + totals.planned_tentative,
          },
          items,
        };
      };

      const revenueDrawerPayload = computed(() => {
        const range = revenueDrawerScope.value === 'month' ? monthRange.value : plannedRange.value;
        return buildRevenuePayload(range.from, range.to);
      });
      const revenueDrawerSummary = computed(() => revenueDrawerPayload.value.summary);
      const revenueDrawerItems = computed(() => revenueDrawerPayload.value.items);
      const revenueDrawerPeriodLabel = computed(() =>
        revenueDrawerScope.value === 'month' ? monthPeriodLabel.value : plannedPeriodLabel.value
      );

      const openRevenueDrawer = (scope) => {
        revenueDrawerScope.value = scope;
        showRevenueDrawer.value = true;
      };

      const handleRevenueSelect = (item) => {
        showRevenueDrawer.value = false;
        const wo = workOrders.value.find((row) => row.id === item.work_order_id);
        if (wo) {
          openWorkOrderDetail(wo);
          return;
        }
        if (item.work_order_id) {
          router.push(`/work-orders/${item.work_order_id}`);
        }
      };

      const refreshWorkOrders = async () => {
        loadedRanges.value.clear();
        workOrders.value = [];

        // Load whichever month(s) the user is currently looking at —
        // not "today" — so a manual refresh after navigating to e.g.
        // June still shows June's data.
        await Promise.all([loadMonthsForCurrentView(), loadResourceConflicts()]);

        // Also load unplanned work orders
        try {
          const unplannedResponse = await workOrderService.getAllWorkOrdersPaginated({
            unplanned: true,
          });
          const unplannedData = unplannedResponse.workOrders || [];

          const existingIds = new Set(workOrders.value.map((wo) => wo.id));
          const newUnplanned = unplannedData.filter((wo) => !existingIds.has(wo.id));
          workOrders.value = [...workOrders.value, ...newUnplanned];
        } catch (error) {
          console.error('Error loading unplanned work orders:', error);
        }
      };

      // Lazy-load any month the user navigates into. We only actually
      // hit the network for months we haven't already cached; the
      // `loadedRanges` guard inside `loadWorkOrdersForRange` keeps the
      // watcher cheap even on rapid prev/next clicks.
      watch(selectedDayDate, () => {
        if (currentView.value === 'day') {
          loadMonthForDate(selectedDayDate.value);
          loadResourceConflicts();
        }
      });

      watch(weekStartDate, () => {
        if (currentView.value === 'week') {
          loadMonthsForCurrentView();
          loadResourceConflicts();
        }
      });

      watch(currentView, () => {
        loadMonthsForCurrentView();
        loadResourceConflicts();
      });

      const selectWorkOrder = (wo) => {
        selectedWorkOrder.value = wo;
      };

      const closeDetails = () => {
        selectedWorkOrder.value = null;
      };

      const goToWorkOrder = () => {
        if (selectedWorkOrder.value) {
          openWorkOrderDetail(selectedWorkOrder.value);
        }
      };

      const openWorkOrderDetail = (workOrder) => {
        uiStore.openDrawer(WorkOrderDetailDrawer, {
          workOrder,
          onUpdate: async (updates) => {
            try {
              await workOrderService.updateWorkOrder(workOrder.id, updates);
              const idx = workOrders.value.findIndex((wo) => wo.id === workOrder.id);
              if (idx !== -1) {
                workOrders.value[idx] = { ...workOrders.value[idx], ...updates };
              }
              updateCalendarEvents();
            } catch (error) {
              console.error('Failed to update work order:', error);
              toastStore.addToast({ message: 'Kon werkbon niet opslaan', type: 'error' });
            }
          },
        });
      };

      // Klik-actie op een event in de planning-grid:
      // - geen monteur toegewezen → schedule-modal om snel monteur + tijd te kiezen
      // - voorlopig (alleen tentative date, nog niet bevestigd met klant)
      //   → schedule-modal in "Bevestigen"-stand (zie openScheduleModal)
      // - bevestigd & toegewezen → volledig detail-drawer
      const handleEventClick = (workOrder) => {
        const isUnassigned = !(workOrder?.assigned_to?.id || workOrder?.assigned_to);
        const isTentativeOnly = isTentativelyPlanned(workOrder);
        if (isUnassigned || isTentativeOnly) {
          openScheduleModal(workOrder);
        } else {
          openWorkOrderDetail(workOrder);
        }
      };

      // Drag and drop from sidebar
      const handleDragStart = (event, workOrder) => {
        draggingWorkOrder.value = workOrder;
        event.dataTransfer.setData('workOrderId', workOrder.id);
        event.dataTransfer.effectAllowed = 'move';
        // De bronkaart pas ná deze tick verbergen: verdwijnt het element
        // synchroon tijdens dragstart, dan annuleert de browser de drag.
        setTimeout(() => {
          if (draggingWorkOrder.value?.id === workOrder.id) {
            dragSourceHiddenId.value = workOrder.id;
          }
        }, 0);
      };

      const handleDragEnd = () => {
        clearDragState();
      };

      // Open schedule modal for a work order
      const openScheduleModal = (workOrder) => {
        workOrderToSchedule.value = workOrder;

        // Bij een voorlopige werkbon is de meest waarschijnlijke vervolgactie
        // 'bevestigen', dus we openen de modal in die stand. Voor nieuwe of
        // geannuleerde werkbonnen laten we de modal zelf de default bepalen.
        const isTentativeOnly = isTentativelyPlanned(workOrder);
        const modalTitle = isTentativeOnly ? 'Werkbon bevestigen' : 'Werkbon inplannen';

        uiStore.addModal(ScheduleWorkOrderModal, {
          title: modalTitle,
          workOrder: workOrder,
          installerOptions: installerOptions.value,
          existingWorkOrders: workOrders.value,
          defaultPlanningType: isTentativeOnly ? 'confirmed' : null,
          onSubmit: async (formData) => {
            // Combine date and time into ISO string
            const appointmentDate = new Date(`${formData.date}T${formData.startTime}:00`);
            const isTentative = formData.planningType === 'tentative';

            // Hoofdmonteur + extra monteurs samenvoegen tot één lijst zonder
            // duplicaten — dit is wat de backend als unie van assignees verwacht.
            const assignedUserIds = [
              ...(formData.installerId ? [formData.installerId] : []),
              ...(formData.extraInstallerIds || []).filter(
                (id) => id && id !== formData.installerId
              ),
            ];

            try {
              const isoDate = appointmentDate.toISOString();
              const appointmentPayload = buildAppointmentPayload(workOrder, {
                startIso: isoDate,
                durationMinutes: formData.duration || 120,
                confirmed: !isTentative,
              });
              const updateData = {
                ...appointmentPayload,
                assigned_to: formData.installerId || null,
                assigned_user_ids: assignedUserIds,
                status: statusAfterScheduling(workOrder),
                allow_conflict: formData.allowConflict === true,
              };

              await workOrderService.updateWorkOrder(workOrder.id, updateData);

              // Update local state
              const idx = workOrders.value.findIndex((wo) => wo.id === workOrder.id);
              if (idx !== -1) {
                applyAppointmentLocal(workOrders.value[idx], appointmentPayload);
                workOrders.value[idx].assigned_to = formData.installerId;
                workOrders.value[idx].assigned_user_ids = assignedUserIds;
                // `assigned_users` (objecten) wordt door de backend teruggegeven;
                // we updaten een minimale shape zodat matchesInstaller direct werkt.
                workOrders.value[idx].assigned_users = assignedUserIds.map((id) => ({ id }));
              }

              updateCalendarEvents();

              toastStore.addToast({
                message: isTentative ? 'Werkbon voorlopig ingepland' : 'Werkbon bevestigd',
                type: 'success',
              });

              uiStore.closeAllModals();
            } catch (error) {
              console.error('Error scheduling work order:', error);
              toastStore.addToast({
                message: scheduleErrorMessage(error),
                type: 'error',
              });
            }
          },
        });
      };

      onMounted(async () => {
        // Vangnet voor drag-state: als de kaart na een geslaagde drop uit de
        // DOM verdwijnt (re-render), vuurt dragend niet meer op het bron-
        // element zelf. Document-breed luisteren garandeert het opruimen van
        // de ghost en cel-highlights, ook bij een geannuleerde drag.
        document.addEventListener('dragend', clearDragState);
        document.addEventListener('drop', clearDragState);

        await Promise.all([loadInstallers(), refreshWorkOrders()]);

        // Tippy for filter dropdowns
        if (monteursFilterRef.value && monteursFilterPanelRef.value) {
          monteursFilterTippy = tippy(monteursFilterRef.value, {
            content: monteursFilterPanelRef.value,
            interactive: true,
            trigger: 'click',
            placement: 'bottom-start',
            animation: 'shift-away',
            theme: 'dropdown',
            appendTo: () => document.body,
            onShow() {
              monteursFilterOpen.value = true;
            },
            onHide() {
              monteursFilterOpen.value = false;
            },
          });
        }
        if (statusFilterRef.value && statusFilterPanelRef.value) {
          statusFilterTippy = tippy(statusFilterRef.value, {
            content: statusFilterPanelRef.value,
            interactive: true,
            trigger: 'click',
            placement: 'bottom-start',
            animation: 'shift-away',
            theme: 'dropdown',
            appendTo: () => document.body,
            onShow() {
              statusFilterOpen.value = true;
            },
            onHide() {
              statusFilterOpen.value = false;
            },
          });
        }
        if (workTypeFilterRef.value && workTypeFilterPanelRef.value) {
          workTypeFilterTippy = tippy(workTypeFilterRef.value, {
            content: workTypeFilterPanelRef.value,
            interactive: true,
            trigger: 'click',
            placement: 'bottom-start',
            animation: 'shift-away',
            theme: 'dropdown',
            appendTo: () => document.body,
            onShow() {
              workTypeFilterOpen.value = true;
            },
            onHide() {
              workTypeFilterOpen.value = false;
            },
          });
        }
      });

      onBeforeUnmount(() => {
        document.removeEventListener('dragend', clearDragState);
        document.removeEventListener('drop', clearDragState);
        monteursFilterTippy?.destroy();
        statusFilterTippy?.destroy();
        workTypeFilterTippy?.destroy();
      });

      return {
        workOrders,
        selectedWorkOrder,
        loading,
        pendingSlotDate,
        selectedDayDate,
        toggleUnplannedPanel,
        isDraggingPlannedWorkOrder,
        handleUnscheduleDrop,
        dragWindowCellClass,
        dragWindowCellTitle,
        getWindowDemand,
        showUnplannedDrawer,
        showRevenueDrawer,
        plannedViewValue,
        plannedMonthValue,
        plannedAvgPerDay,
        revenueDrawerSummary,
        revenueDrawerItems,
        revenueDrawerPeriodLabel,
        openRevenueDrawer,
        handleRevenueSelect,
        formatCurrency,
        planningQueueCount,
        planningQueueButtonLabel,
        planningQueueTooltip,
        unplannedWorkOrders,
        tentativeWorkOrders,
        currentView,
        draggingWorkOrder,
        installers,
        installerOptions,
        installersWithUnassigned,
        visibleInstallersForGrid,
        statusLegendItems,
        isInstallerVisible,
        toggleInstaller,
        getWorkOrderCountForInstaller,
        monteursDropdownLabel,
        statusDropdownLabel,
        monteursFilterRef,
        monteursFilterPanelRef,
        monteursFilterOpen,
        statusFilterRef,
        statusFilterPanelRef,
        statusFilterOpen,
        workTypeFilterRef,
        workTypeFilterPanelRef,
        workTypeFilterOpen,
        workTypeLegendItems,
        visibleWorkTypeKeys,
        workTypeDropdownLabel,
        isWorkTypeVisible,
        toggleWorkTypeFilter,
        showAllWorkTypes,
        hideAllWorkTypes,
        getWorkOrderCountForWorkType,
        hiddenInstallerIds,
        showAllInstallers,
        hideAllInstallers,
        showAllStatuses,
        hideAllStatuses,
        dayViewTitle,
        weekViewTitle,
        dayTimeSlots,
        weekDaysForView,
        getInitials,
        getWorkOrderCountForDay,
        getWorkOrderCountForWeek,
        getWorkOrdersForDayCell,
        dayEventLaneStyle,
        dayCellOffClass,
        dayHeaderHours,
        isTechDayOff,
        getDayFreeCapacity,
        getWorkOrdersForWeekCell,
        navigateDay,
        navigateWeek,
        goToTodayView,
        onDayCellClick,
        onDayCellDragOver,
        onDayCellDrop,
        onWeekCellClick,
        onWeekCellDragOver,
        onWeekCellDrop,
        onCalendarDragLeave,
        dragGhost,
        dragSourceHiddenId,
        // Methods
        getStatusLabel,
        getInstallerName,
        formatDateTime,
        refreshWorkOrders,
        selectWorkOrder,
        closeDetails,
        goToWorkOrder,
        openWorkOrderDetail,
        handleEventClick,
        isAdmin,
        visibleStatusKeys,
        isStatusVisible,
        toggleStatusFilter,
        handleDragStart,
        handleDragEnd,
        switchView,
        openScheduleModal,
        pendingSlot,
        pendingSlotLabel,
        closeUnplannedDrawer,
        handlePickWorkOrder,
        filteredUnplannedForDrawer,
        filteredTentativeForDrawer,
        selectedWorkOrderIds,
        selectionSummary,
        weekStartIso,
        resourceConflictCount,
        visibleTechnicianUserIds,
        bulkLoading,
        bulkActiveAction,
        toggleWorkOrderSelection,
        setWorkOrderSelection,
        addWorkOrderSelection,
        removeWorkOrderSelection,
        clearWorkOrderSelection,
        handleBulkConfirm,
        handleBulkDelete,
        handleDeleteWorkOrder,
        handleOpenBulkPlanModal,
        handlePreviewPlan,
        goToServiceNotifications,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .planning-page {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-height: 0;
    background: var(--slate-50);
  }

  .planning-toolbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;
    background: white;
    border-bottom: 1px solid var(--slate-200);

    &__left {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;
    }

    &__right {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__title {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__date {
      font-size: 0.9375rem;
      color: var(--slate-700);
      min-width: 10rem;
      text-align: center;
    }
  }

  .planning-toolbar {
    &__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--slate-600);
      cursor: pointer;

      &:hover {
        background: var(--slate-100);
        color: var(--slate-900);
      }
    }
  }

  .toolbar-nav-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: var(--slate-100);
    border: 1px solid var(--slate-200);
    border-radius: 0.375rem;
    cursor: pointer;
    color: var(--slate-700);
    font-size: 1.25rem;

    &:hover {
      background: var(--slate-200);
    }

    &--today {
      width: auto;
      padding: 0 0.75rem;
      font-size: 0.875rem;
      font-weight: 500;
    }
  }

  // Kalender links, gedockt "Te plannen"-paneel rechts. Het paneel is een
  // flex-sibling (geen overlay) zodat drag & drop tussen beide werkt.
  .planning-page__content {
    margin-top: 1rem;
    display: flex;
    align-items: stretch;
    gap: 1rem;
    // Zelfde hoogte als de kalender (max-height hieronder) zodat paneel en
    // kalender gelijk eindigen en het paneel intern scrollt.
    max-height: calc(100vh - 12rem);

    @include lg-down {
      display: block;
      max-height: none;
    }
  }

  .planning-page__calendar {
    background: white;
    border-radius: 0.5rem;
    border: 1px solid var(--slate-200);
    padding: 1rem;
    min-height: 400px;
    max-height: calc(100vh - 16rem);
    overflow: auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .planning-page__main {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
  }

  .planning-page__team {
    padding: 1rem;
    display: flex;
    justify-content: center;
  }

  .planning-top-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0;
  }

  .planning-filter-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.625rem;
    border: 1px solid var(--slate-200);
    border-radius: 6px;
    background: white;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--slate-700);
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s;
    white-space: nowrap;

    @include hover {
      background: var(--slate-50);
      border-color: var(--slate-300);
    }

    &--active {
      background: var(--slate-800);
      border-color: var(--slate-800);
      color: white;

      @include hover {
        background: var(--slate-700);
        border-color: var(--slate-700);
      }
    }

    &__chevron {
      transition: transform 0.2s ease;
      transform: rotate(180deg);
      color: var(--slate-400);

      &--open {
        transform: rotate(0deg);
      }
    }
  }

  .sidebar-monteurs-list {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .sidebar-monteur-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0;
    border-bottom: 1px solid var(--slate-100);
    cursor: pointer;
    transition: background 0.2s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--slate-50);
    }

    &--inactive {
      opacity: 0.5;
      .sidebar-monteur-item__name,
      .sidebar-monteur-item__week {
        color: var(--slate-400);
      }
    }

    &__avatar {
      display: inline-block;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__info {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    &__name {
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--slate-900);
    }

    &__week {
      font-size: 0.75rem;
      color: var(--slate-500);
    }
  }

  .engineer-more-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    border-radius: 0.375rem;
    border: 1px solid var(--slate-200);
    background: var(--slate-50);
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--slate-700);
    cursor: pointer;

    &:hover {
      background: var(--slate-100);
    }

    &--open {
      background: var(--slate-100);
      border-color: var(--slate-300);
    }
  }

  .engineer-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 0.25rem;
    min-width: 200px;
    max-height: 280px;
    overflow-y: auto;
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 50;
    padding: 0.5rem;
  }

  .engineer-dropdown__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
    cursor: pointer;
    font-size: 0.8125rem;

    &:hover {
      background: var(--slate-50);
    }
  }

  .engineer-dropdown__checkbox {
    margin: 0;
  }

  .engineer-dropdown__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .engineer-dropdown__label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .engineer-dropdown__count {
    font-size: 0.75rem;
    color: var(--slate-500);
  }

  .planning-toolbar__btn--badge {
    font-weight: 600;
  }

  .planning-toolbar__btn--warn {
    border-color: var(--amber-300) !important;
    color: var(--amber-800) !important;
    background: var(--amber-50) !important;
  }

  .planning-grid-wrap {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .planning-grid-header {
    display: flex;
    align-items: center;
    gap: 1rem;

    &--hidden {
      display: none;
    }
  }

  .planning-grid-title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--slate-900);
  }

  .planning-grid-header .nav-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: var(--slate-100);
    border: 1px solid var(--slate-200);
    border-radius: 0.375rem;
    cursor: pointer;
    color: var(--slate-700);
    font-size: 1.25rem;
    line-height: 1;

    &:hover {
      background: var(--slate-200);
    }

    &--today {
      width: auto;
      padding: 0 1rem;
      font-size: 0.875rem;
      font-weight: 500;
    }
  }

  .planning-grid {
    display: grid;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    overflow: scroll;
    min-width: 0;
  }

  .planning-grid--day {
    grid-template-columns: 56px repeat(var(--day-cols, 4), minmax(140px, 1fr));
  }

  .planning-grid--week {
    display: flex;
    flex-direction: column;

    .planning-grid__row {
      display: grid;
      grid-template-columns: 140px repeat(7, minmax(120px, 1fr));
    }

    .planning-grid__row--header {
      position: sticky;
      top: 0;
      z-index: 10;
    }
  }

  .planning-grid__row {
    display: contents;
  }

  .planning-grid__row--header {
    .planning-grid__cell {
      background: var(--slate-100);
      font-weight: 600;
      font-size: 0.8125rem;
      color: var(--slate-700);
      padding: 0.75rem;
      border-bottom: 2px solid var(--slate-300);
    }
  }

  // ── Dag view kalender ──────────────────────────────────────────────────────
  .day-cal {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    overflow: hidden;
    min-width: 0;
  }

  .day-cal__header {
    display: grid;
    grid-template-columns: 56px repeat(var(--day-cols, 4), minmax(140px, 1fr));
    background: var(--slate-100);
    border-bottom: 2px solid var(--slate-300);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .day-cal__header-time {
    border-right: 1px solid var(--slate-200);
  }

  .day-cal__header-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.125rem;
    padding: 0.625rem 0.5rem;
    font-weight: 600;
    font-size: 0.8125rem;
    color: var(--slate-700);
    border-right: 1px solid var(--slate-200);
    text-align: center;

    &:last-child {
      border-right: none;
    }
  }

  .day-cal__body {
    overflow-y: auto;
    max-height: calc(100vh - 220px);
    background: white;
  }

  .day-cal__grid {
    display: grid;
    grid-template-columns: 56px repeat(var(--day-cols, 4), minmax(140px, 1fr));
  }

  $slot-height: 24px;

  .day-cal__time {
    height: $slot-height;
    background: var(--slate-50);
    border-right: 1px solid var(--slate-200);
    border-bottom: 1px solid transparent;
    font-size: 0.7rem;
    color: var(--slate-400);
    display: flex;
    align-items: flex-start;
    padding: 0 0.375rem;

    span {
      transform: translateY(-0.6em);
      display: block;
    }

    &--hour {
      border-bottom: 1px solid var(--slate-200);
    }

    &--half {
      border-bottom: 1px dashed var(--slate-200);
    }
  }

  .day-cal__cell {
    height: $slot-height;
    border-right: 1px solid var(--slate-200);
    border-bottom: 1px solid transparent;
    cursor: pointer;
    position: relative;
    overflow: visible;
    transition: background 0.1s;

    &:last-child {
      border-right: none;
    }

    &--hour {
      border-bottom: 1px solid var(--slate-200);
    }

    &--half {
      border-bottom: 1px dashed var(--slate-200);
    }

    &--dragover:hover {
      background: var(--slate-50);
    }

    // Buiten de werktijd van deze monteur (per kolom), lunch, of hele vrije dag.
    &--off-hours {
      background-color: var(--slate-50);
      background-image: repeating-linear-gradient(
        135deg,
        transparent 0 7px,
        rgba(100, 116, 139, 0.07) 7px 9px
      );
    }
    &--lunch {
      background-color: var(--slate-50);
    }
    &--day-off {
      background-color: var(--slate-100);
      background-image: repeating-linear-gradient(
        135deg,
        transparent 0 7px,
        rgba(100, 116, 139, 0.1) 7px 9px
      );
    }

    // Klantvenster-feedback tijdens slepen
    &--window-ok {
      background: rgba(34, 197, 94, 0.1);
    }
    &--window-warn {
      background: rgba(245, 158, 11, 0.12);
    }
    &--window-blocked {
      background: rgba(239, 68, 68, 0.1);
      background-image: repeating-linear-gradient(
        135deg,
        transparent 0 6px,
        rgba(239, 68, 68, 0.08) 6px 8px
      );
    }
  }

  // Dag-view events: absolute gepositioneerd vanuit de startcel, steken omlaag uit.
  // Dubbele class: .planning-event (verderop) zet position: relative en zou
  // anders winnen, waardoor kaarten in dezelfde startcel onder elkaar stapelen.
  .planning-event.planning-event--day {
    position: absolute;
    top: 0;
    left: 2px;
    right: 2px;
    z-index: 5;
    overflow: hidden;
  }

  .planning-grid__cell {
    padding: 0.25rem;
    border-right: 1px solid var(--slate-200);
    border-bottom: 1px solid var(--slate-200);
    min-height: 52px;

    &:last-child {
      border-right: none;
    }

    // Vrije dag van deze monteur (weekweergave).
    &--day-off {
      background-color: var(--slate-100);
      background-image: repeating-linear-gradient(
        135deg,
        transparent 0 7px,
        rgba(100, 116, 139, 0.1) 7px 9px
      );
    }

    &--time-label {
      font-size: 0.75rem;
      color: var(--slate-500);
      background: var(--slate-50);
      display: flex;
      align-items: flex-start;
      padding-top: 0.625rem;
    }

    &--resource-label {
      font-size: 0.8125rem;
      color: var(--slate-700);
      background: var(--slate-50);
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    &--header {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.125rem;
      text-align: center;
    }

    &--today {
      background: rgba(59, 130, 246, 0.08);
    }

    &--drop {
      cursor: pointer;
      transition: background 0.15s;

      &:hover,
      &.planning-grid__cell--drop-active {
        background: var(--slate-50);
      }
    }

    &--pending {
      background: var(--brand-50) !important;
    }

    // Klantvenster-feedback tijdens slepen (overrulet de neutrale drop-tint)
    &--window-ok,
    &--window-ok.planning-grid__cell--drop-active {
      background: rgba(34, 197, 94, 0.12);
    }
    &--window-warn,
    &--window-warn.planning-grid__cell--drop-active {
      background: rgba(245, 158, 11, 0.14);
    }
    &--window-blocked,
    &--window-blocked.planning-grid__cell--drop-active {
      background-color: rgba(239, 68, 68, 0.1);
      background-image: repeating-linear-gradient(
        135deg,
        transparent 0 6px,
        rgba(239, 68, 68, 0.08) 6px 8px
      );
    }
  }

  .cell-header__demand {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    margin-top: 0.125rem;
    padding: 0.0625rem 0.375rem;
    border-radius: 999px;
    font-size: 0.625rem;
    font-weight: 600;
    background: var(--amber-50);
    color: var(--amber-700);
    cursor: help;

    &--hard {
      background: var(--red-50);
      color: var(--red-700);
    }
  }

  .cell-header__dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .cell-header__name {
    font-size: 0.75rem;
  }

  .cell-header__count {
    font-size: 0.6875rem;
    color: var(--slate-500);
    font-weight: 400;
  }

  // Werktijd van de monteur in de dag-kolomkop
  .cell-header__hours {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: var(--slate-400);
    font-variant-numeric: tabular-nums;
    cursor: help;

    &--custom {
      color: var(--brand-600);
    }

    &--off {
      color: var(--slate-500);
      font-style: italic;
    }
  }

  // Vrije capaciteit in de week-dagkop
  .cell-header__capacity {
    display: inline-block;
    margin-top: 0.125rem;
    padding: 0.0625rem 0.375rem;
    border-radius: 999px;
    font-size: 0.625rem;
    font-weight: 600;
    background: var(--emerald-50);
    color: var(--emerald-600);
    cursor: help;

    &--empty {
      background: var(--slate-100);
      color: var(--slate-500);
    }
  }

  .cell-header__day {
    font-size: 0.6875rem;
    color: var(--slate-500);
  }

  .cell-header__date {
    font-size: 0.8125rem;
  }

  .resource-label__name {
    font-weight: 500;
  }

  .resource-label__count {
    font-size: 0.75rem;
    color: var(--slate-500);
  }

  .planning-event {
    position: relative;
    padding: 0.375rem 0.5rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
    cursor: grab;
    border: 2px solid transparent;
    transition:
      filter 0.15s ease,
      box-shadow 0.15s ease;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      filter: brightness(0.95);
    }

    &:active {
      cursor: grabbing;
    }

    // Werkbonnen zonder monteur: extra opvallend zodat ze direct als
    // "actie nodig" leesbaar zijn (en niet wegvallen tegen ingeplande events).
    &--unassigned {
      box-shadow: inset 0 0 0 1px var(--slate-400);

      &:hover {
        box-shadow:
          inset 0 0 0 1px var(--slate-500),
          0 2px 6px rgba(0, 0, 0, 0.08);
      }
    }

    &--tentative {
      border-style: dashed !important;
      border-width: 2px !important;
      // Diagonale hatch om "concept"-karakter te benadrukken
      background-image: repeating-linear-gradient(
        135deg,
        rgba(255, 255, 255, 0) 0,
        rgba(255, 255, 255, 0) 6px,
        rgba(255, 255, 255, 0.45) 6px,
        rgba(255, 255, 255, 0.45) 8px
      );
      // Iets desaturated zodat het optisch terugvalt t.o.v. bevestigde werkbonnen
      filter: saturate(0.75);

      &:hover {
        filter: saturate(0.85) brightness(0.97);
      }
    }

    // Hulpmonteur-rol: subtieler, om aan te geven dat de hoofdmonteur
    // van deze werkbon in een andere kolom staat. Niet zo dempend als
    // tentative, want de bon is wel degelijk bevestigd.
    &--helper {
      opacity: 0.85;
      box-shadow: inset 3px 0 0 0 rgba(100, 116, 139, 0.55);

      &:hover {
        opacity: 1;
      }
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      padding: 0.05rem 0.35rem;
      background: var(--amber-100);
      color: var(--amber-800);
      border: 1px dashed var(--amber-500);
      border-radius: 9999px;
      font-size: 0.625rem;
      font-weight: 600;
      line-height: 1.4;
      text-transform: uppercase;
      letter-spacing: 0.02em;

      &--helper {
        background: var(--slate-100);
        color: var(--slate-700);
        border: 1px solid var(--slate-300);
      }
    }

    // Container voor badges + mini-indicatoren, rechtsboven op de kaart
    &__indicators {
      position: absolute;
      top: 4px;
      right: 4px;
      display: inline-flex;
      align-items: center;
      gap: 0.2rem;
      pointer-events: none;
      z-index: 1;
    }

    // Ronde mini-indicator (vinkje = bevestigd met klant, doos = materialen)
    &__indicator {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 15px;
      height: 15px;
      border-radius: 9999px;
      background: var(--green-100);
      color: var(--green-700);
      border: 1px solid var(--green-300);

      &--materials-waiting {
        background: rgba(255, 255, 255, 0.6);
        color: var(--slate-500);
        border-color: var(--slate-300);
        opacity: 0.55;
      }

      // Dubbel geboekt: deze kaart deelt tijd met een andere van dezelfde monteur
      &--overlap {
        background: var(--red-100);
        color: var(--red-700);
        border-color: var(--red-300);
      }

      // Materieelconflict: meer gevraagd dan beschikbaar op deze dag
      &--resource {
        background: var(--amber-100);
        color: var(--amber-800);
        border-color: var(--amber-300);
      }
    }

    &__customer {
      display: block;
      font-weight: 600;
      line-height: 1.3;
    }

    // Geef tekst wat ruimte voor de badges/indicatoren zodat ze niet overlappen
    &--tentative &__customer,
    &--helper &__customer {
      padding-right: 4rem;
    }

    &--has-indicators:not(&--tentative):not(&--helper) &__customer {
      padding-right: 2.25rem;
    }

    &__time {
      display: block;
      font-size: 0.6875rem;
      opacity: 0.8;
      margin-top: 0.125rem;
    }

    // Snelle "verzetten"-knop — alleen zichtbaar bij hover over het event
    &__edit {
      position: absolute;
      bottom: 4px;
      right: 4px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      padding: 0;
      border: none;
      border-radius: 0.25rem;
      background: rgba(255, 255, 255, 0.85);
      color: var(--slate-600);
      cursor: pointer;
      opacity: 0;
      transition:
        opacity 0.15s ease,
        background 0.15s ease;

      &:hover {
        background: white;
        color: var(--slate-900);
      }
    }

    @include hover {
      .planning-event__edit {
        opacity: 1;
      }
    }

    &__address {
      display: block;
      font-size: 0.6875rem;
      opacity: 0.7;
      margin-top: 0.125rem;
    }
  }

  // Ghost-preview van de werkbon in de cel waar je overheen sleept —
  // pointer-events uit zodat dragover/drop op de cel zelf blijven landen.
  // Kleuren komen inline mee (status-palette), zodat het eruitziet alsof de
  // kaart zelf van vakje naar vakje springt.
  .planning-event--ghost {
    pointer-events: none;
    z-index: 6;
    opacity: 0.85;
    overflow: hidden;
  }

  // Ghost-staten: waar landt de bon écht?
  .planning-event--ghost-moved {
    // Verschoven naar het eerste vrije gat: gestreepte rand + pijl in de tijd
    border-style: dashed !important;
    border-width: 2px !important;
    border-color: var(--brand-500) !important;
  }
  .planning-event--ghost-outside {
    border-width: 2px !important;
    border-color: var(--amber-500) !important;
    box-shadow: inset 0 0 0 1px rgba(245, 158, 11, 0.35);
  }
  .planning-event--ghost-exact {
    border-width: 2px !important;
    border-color: var(--red-500) !important;
    opacity: 0.7;
  }
  .planning-event--ghost-helpers {
    border-style: dashed !important;
    border-width: 2px !important;
    border-color: var(--amber-500) !important;
    background-color: var(--amber-50) !important;
    color: var(--amber-800) !important;
  }
  .planning-event--ghost-full {
    background: var(--red-50) !important;
    color: var(--red-700) !important;
    border: 2px solid var(--red-400) !important;
    background-image: repeating-linear-gradient(
      135deg,
      transparent 0 6px,
      rgba(239, 68, 68, 0.12) 6px 8px
    ) !important;
  }

  // De originele kaart tijdens het slepen: onzichtbaar, maar wél in de DOM
  // laten staan zodat het dragend-event op het bron-element blijft vuren.
  .planning-event--drag-hidden {
    display: none;
  }

  .planning-cell-hover-plus {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 2px dashed transparent;
    border-radius: 0.375rem;
    color: transparent;
    margin: auto;
    transition: all 0.15s ease;
  }

  .planning-grid__cell--drop:hover .planning-cell-hover-plus,
  .day-cal__cell:hover .planning-cell-hover-plus {
    border-color: var(--slate-300);
    color: var(--slate-400);
  }

  .planning-slot-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    border: 2px dashed var(--brand-300);
    border-radius: 0.375rem;
    color: var(--brand-400);
    background: var(--brand-50);
    min-height: 40px;
    cursor: pointer;
    animation: pulse-border 1.5s ease-in-out infinite;
  }

  @keyframes pulse-border {
    0%,
    100% {
      border-color: var(--brand-300);
    }
    50% {
      border-color: var(--brand-500);
    }
  }

  .sidebar-section {
    &__title {
      margin: 0 0 0.5rem 0;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    &--grow {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }

  .monteurs-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .monteur-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0;
    cursor: pointer;
    font-size: 0.875rem;
    user-select: none;

    &--inactive {
      opacity: 0.5;
    }

    &__checkbox {
      margin: 0;
      cursor: pointer;
    }

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__count {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-500);
      min-width: 1.5rem;
      text-align: right;
    }
  }

  .status-legend {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .status-legend__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    color: var(--slate-600);
  }

  .status-legend__dot {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;

    &--completed {
      background: var(--green-500);
    }
    &--in_progress {
      background: var(--blue-500);
    }
    &--scheduled {
      background: var(--amber-500);
    }
    &--ready_for_installation {
      background: var(--purple-500);
    }
    &--pending {
      background: var(--slate-400);
    }
    &--cancelled {
      background: var(--red-500);
    }
  }

  .view-toggle {
    display: inline-flex;
    background: var(--slate-100);
    border-radius: 0.375rem;
    padding: 0.25rem;
    margin-right: 0.75rem;

    button {
      padding: 0.375rem 0.75rem;
      border: none;
      background: transparent;
      border-radius: 0.25rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-600);
      cursor: pointer;
      transition: all 0.15s ease;

      &:hover {
        color: var(--slate-900);
      }

      &.active {
        background: white;
        color: var(--slate-900);
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      }
    }
  }

  .work-order-panel {
    background: white;
    border-radius: 0.5rem;
    border: 1px solid var(--slate-200);
    overflow: hidden;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem;
      border-bottom: 1px solid var(--slate-100);

      h3 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
      }
    }

    &__content {
      padding: 1rem;
    }

    &__status {
      margin-bottom: 1rem;
    }

    &__section {
      margin-bottom: 1rem;

      h4 {
        margin: 0 0 0.5rem 0;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--slate-500);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    }

    &__actions {
      display: flex;
      gap: 0.5rem;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-100);
    }
  }

  .customer-name {
    margin: 0;
    font-weight: 500;
    color: var(--slate-900);
  }

  .customer-address {
    margin: 0.25rem 0 0 0;
    font-size: 0.875rem;
    color: var(--slate-600);
  }

  .items-list {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      padding: 0.5rem 0;
      border-bottom: 1px solid var(--slate-100);
      font-size: 0.875rem;

      &:last-child {
        border-bottom: none;
      }
    }
  }

  .item-sku {
    display: inline-block;
    margin-left: 0.5rem;
    padding: 0.125rem 0.375rem;
    background: var(--slate-100);
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-family: monospace;
    color: var(--slate-600);
  }

  .unplanned-panel {
    flex: 1;
    min-height: 0;
    overflow-y: auto;

    h3 {
      margin: 0 0 0.25rem 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-700);
    }
  }

  .unplanned-hint {
    margin: 0 0 1rem 0;
    font-size: 0.75rem;
    color: var(--slate-500);
  }

  .unplanned-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 500px;
    overflow-y: auto;
  }

  .unplanned-card {
    padding: 0.75rem;
    background: var(--slate-50);
    border: 1px solid var(--slate-200);
    border-radius: 0.375rem;
    cursor: grab;
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--slate-300);
      background: white;
    }

    &--dragging {
      opacity: 0.5;
      border-color: var(--blue-400);
      background: var(--blue-50);
    }

    &:active {
      cursor: grabbing;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.25rem;
    }

    &__number {
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--slate-900);
    }

    &__customer {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-700);
    }

    &__address {
      margin: 0.25rem 0 0 0;
      font-size: 0.8125rem;
      color: var(--slate-700);
    }

    &__description {
      margin: 0.125rem 0 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__warning {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin: 0.5rem 0 0 0;
      padding: 0.25rem 0.5rem;
      font-size: 0.75rem;
      color: var(--amber-700);
      background: var(--amber-50);
      border-radius: 0.25rem;
    }

    &__actions {
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px solid var(--slate-200);
    }

    &__date {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin: 0.25rem 0 0 0;
      font-size: 0.75rem;
      color: var(--slate-600);
    }

    &--tentative {
      border-style: dashed;
      border-color: var(--amber-400);
      background: var(--amber-50);

      &:hover {
        border-color: var(--amber-500);
        background: var(--amber-100);
      }
    }
  }

  .tentative-section {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--slate-200);

    h3 {
      margin: 0 0 0.25rem 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--amber-700);
    }
  }

  .tentative-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.125rem 0.5rem;
    font-size: 0.6875rem;
    font-weight: 500;
    background: var(--amber-100);
    color: var(--amber-700);
    border-radius: 9999px;
    border: 1px dashed var(--amber-400);
  }

  .empty-unplanned {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 2rem;
    color: var(--green-600);

    p {
      margin: 0;
      font-size: 0.875rem;
    }
  }

  .status-badge {
    display: inline-flex;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;

    &--sm {
      padding: 0.125rem 0.5rem;
      font-size: 0.6875rem;
    }

    &--pending {
      background: var(--slate-100);
      color: var(--slate-700);
    }

    &--scheduled {
      background: var(--blue-100);
      color: var(--blue-700);
    }

    &--ready_for_installation {
      background: var(--amber-100);
      color: var(--amber-700);
    }

    &--in_progress {
      background: var(--purple-100);
      color: var(--purple-700);
    }

    &--completed {
      background: var(--green-100);
      color: var(--green-700);
    }

    &--cancelled {
      background: var(--red-100);
      color: var(--red-700);
    }
  }

  .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    color: var(--slate-500);

    &:hover {
      background: var(--slate-100);
      color: var(--slate-700);
    }
  }

  .text-muted {
    color: var(--slate-500);
  }

  .loading-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    z-index: 100;
  }

  .no-appointments {
    padding: 2rem;
    text-align: center;
    color: var(--slate-400);
    font-size: 0.875rem;
  }
</style>

<style lang="scss">
  // Planning filter panels — rendered via Tippy into document.body
  .tippy-box[data-theme~='dropdown'] {
    background-color: white;
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    box-shadow:
      0 4px 16px rgba(0, 0, 0, 0.1),
      0 1px 3px rgba(0, 0, 0, 0.06);

    .tippy-content {
      padding: 0;
    }
    .tippy-arrow {
      display: none;
    }

    .planning-filter-panel {
      display: block;
      min-width: 200px;
    }
  }

  .planning-filter-panel {
    display: none;
    padding: 0.25rem 0;

    &__actions {
      display: flex;
      gap: 0.25rem;
      padding: 0.25rem 0.5rem 0.375rem;
      border-bottom: 1px solid var(--slate-100);
      margin-bottom: 0.25rem;

      button {
        padding: 0.2rem 0.5rem;
        font-size: 0.75rem;
        font-weight: 500;
        color: var(--slate-500);
        background: none;
        border: 1px solid var(--slate-200);
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.1s;

        &:hover {
          background: var(--slate-50);
          color: var(--slate-700);
        }
      }
    }
  }

  .planning-filter-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.45rem 0.75rem;
    cursor: pointer;
    transition:
      background 0.1s,
      opacity 0.15s;
    font-size: 0.875rem;
    color: var(--slate-700);

    &:hover {
      background: var(--slate-50);
    }

    &--inactive {
      opacity: 0.35;
    }

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;

      // Installer dots — inline style handles color
      // Status dots — class-based colors
      &--completed {
        background: var(--green-500);
      }
      &--in_progress {
        background: var(--blue-500);
      }
      &--scheduled {
        background: var(--amber-500);
      }
      &--ready_for_installation {
        background: var(--amber-400);
      }
      &--pending {
        background: var(--slate-400);
      }
      &--cancelled {
        background: var(--red-400);
      }
      &--installation {
        background: var(--brand-500);
      }
      &--measurement {
        background: var(--purple-500);
      }
      &--service {
        background: var(--cyan-500);
      }
    }

    &__label {
      flex: 1;
      white-space: nowrap;
    }

    &__count {
      font-size: 0.75rem;
      color: var(--slate-400);
      font-weight: 400;
    }
  }
</style>
